import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, MoreHorizontal, Calendar, Building2, Filter, Search, Flame, ThumbsUp, ThumbsDown, AlertCircle, ChevronDown, SortAsc, List, ChevronLeft, ChevronRight, ArrowUpDown, MapPin, Mail, Check, Loader2, Activity, Globe, FileText, Settings, X, Save, Clock, CalendarDays, Ban, Trophy, MinusCircle, FileCheck, MonitorCheck, ClipboardCheck, Lock, PlayCircle, XCircle, AlertTriangle, Zap, Dices, ShoppingBag, ArrowRight, Gavel, CheckSquare, FilterX, DollarSign, ChevronUp, Plus, Users, Hourglass, Upload, Sparkles, Download, Paperclip } from 'lucide-react';

import { settingsService } from '../services/settingsService';
import { Tender, TenderPriority, TenderStatus, ExternalTender } from '../types';
import ExternalTenderList from './ExternalTenderList';
import { logActivity, sendEmailNotification } from '../services/auditService';
import { apiService, getStatusKey } from '../services/apiService';
import { theme } from '../config/theme';
import { useModal } from './ModalProvider';
import TruncatedCell from './TruncatedCell';
import PortalCell from './PortalCell';
import MoneyInput from './inputs/MoneyInput';
import { parseBRL, formatBRLCurrency, toFiniteNumber } from '../utils/money';
import { daysUntilCalendarDate, formatCalendarDate, parseCalendarDate } from '../utils/date';
import { getAuthHeaders } from '../config/api';
import './tender-list.css';
import './tenderTable.css';
import './licitacoes-redesign.css';


const EMAIL_SETTINGS_KEY = 'licitaflow_alert_emails';
const BRAZIL_STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const PORTALS = ['ComprasNet', 'Licitações-E', 'Portal de Compras Públicas', 'BLL Compras', 'Conlicitação', 'BEC-SP', 'Outros'];

const fetch = (input: RequestInfo | URL, init: RequestInit = {}) =>
  globalThis.fetch(input, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init.headers || {}) },
  });



interface StatusSelectProps {
  status: TenderStatus;
  onChange: (newStatus: TenderStatus) => void;
  options?: TenderStatus[];
}

const StatusSelect: React.FC<StatusSelectProps> = ({ status, onChange, options }) => {

  // Configuração visual centralizada para cada status
  const statusConfig: Record<TenderStatus, { icon: React.ReactNode, style: string }> = {
  [TenderStatus.EXTREME_PRIORITY]: {
    icon: <Flame className="w-3.5 h-3.5 fill-red-500 text-red-600 dark:text-red-300 animate-pulse" />,
    style: `${theme.classes.badge.error} font-bold shadow-sm`
  },
  [TenderStatus.HIGH_PRIORITY]: {
    icon: <AlertTriangle className="w-3.5 h-3.5 text-orange-600 dark:text-orange-300 fill-orange-100" />,
    style: `${theme.classes.badge.primary} font-semibold`
  },
  [TenderStatus.PARTICIPATE]: {
    icon: <ThumbsUp className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300" />,
    style: `${theme.classes.badge.info} font-semibold`
  },
  [TenderStatus.WON]: {
    icon: <Trophy className="w-3.5 h-3.5 text-emerald-600 fill-emerald-100" />,
    style: `${theme.classes.badge.success} font-bold`
  },
  [TenderStatus.ANALYSIS]: {
    icon: <Search className="w-3.5 h-3.5 text-amber-600" />,
    style: `${theme.classes.badge.warning}`
  },
  [TenderStatus.OPEN]: {
    icon: <PlayCircle className="w-3.5 h-3.5 text-sky-600" />,
    style: `${theme.classes.badge.info}`
  },
  [TenderStatus.CLOSED]: {
    icon: <Lock className="w-3.5 h-3.5 text-slate-500" />,
    style: `${theme.classes.badge.neutral}`
  },
  [TenderStatus.LOST]: {
    icon: <XCircle className="w-3.5 h-3.5 text-rose-500" />,
    style: `${theme.classes.badge.error} opacity-80`
  },
    [TenderStatus.NO_GO]: {
        icon: <Ban className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />,
        style: "bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark border-line dark:border-line-dark line-through decoration-gray-400"
    },
    [TenderStatus.ELECTRONIC_QUOTE]: {
        icon: <Zap className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-300 dark:text-indigo-300 fill-indigo-100" />,
        style: "bg-indigo-50 text-indigo-700 dark:text-indigo-300 border-indigo-200"
    },
    [TenderStatus.DIRECT_WAIVER]: {
        icon: <Gavel className="w-3.5 h-3.5 text-teal-600 dark:text-teal-300 dark:text-teal-300" />,
        style: "bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200"
    },
    [TenderStatus.RANDOM]: {
        icon: <Dices className="w-3.5 h-3.5 text-fuchsia-600 dark:text-fuchsia-300" />,
        style: "bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-200"
    }
  };

  const currentConfig = statusConfig[status] || statusConfig[TenderStatus.OPEN];
  const displayOptions = options || Object.values(TenderStatus);

  return (
    <div className="relative group min-w-[120px] lg:min-w-[140px] xl:min-w-[160px]">
      <div className={`relative flex items-center gap-1.5 xl:gap-2 px-2 xl:px-3 py-1 xl:py-1.5 rounded-lg text-[10px] xl:text-xs border transition-all cursor-pointer hover:shadow-md priority-badge ${currentConfig.style}`}>
        <div className="flex-shrink-0">
            {currentConfig.icon}
        </div>
        <select
          value={status}
          onChange={(e) => onChange(e.target.value as TenderStatus)}
          className="appearance-none bg-transparent border-none outline-none text-inherit font-inherit cursor-pointer w-full pr-5 xl:pr-6 z-10 focus:ring-0 uppercase tracking-tight font-medium"
        >
          {displayOptions.map((s) => (
            <option key={s} value={s} className="bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark normal-case">
              {s}
            </option>
          ))}
        </select>
        <ChevronDown className="w-2.5 h-2.5 xl:w-3 xl:h-3 absolute right-1.5 xl:right-2 pointer-events-none opacity-50 group-hover:opacity-100" />
      </div>
    </div>
  );
};

const PRIORITY_OPTIONS: Array<{
  value: TenderPriority;
  label: string;
  icon: React.ReactNode;
  style: string;
}> = [
  { value: 'NONE', label: 'Sem prioridade', icon: <MinusCircle className="w-3.5 h-3.5" />, style: 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark border-line dark:border-line-dark' },
  { value: 'PRIORITY', label: 'Prioridade', icon: <ThumbsUp className="w-3.5 h-3.5" />, style: `${theme.classes.badge.info} font-semibold` },
  { value: 'VERY_HIGH', label: 'Altíssima prioridade', icon: <AlertTriangle className="w-3.5 h-3.5" />, style: `${theme.classes.badge.primary} font-semibold` },
  { value: 'ULTRA', label: 'Ultra prioridade', icon: <Flame className="w-3.5 h-3.5 fill-red-500 text-red-600 dark:text-red-300" />, style: `${theme.classes.badge.error} font-bold` },
  { value: 'NO_GO', label: 'Não participar', icon: <Ban className="w-3.5 h-3.5" />, style: 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark border-line dark:border-line-dark line-through decoration-gray-400' },
];

interface PrioritySelectProps {
  priority?: TenderPriority;
  onChange: (priority: TenderPriority) => void;
}

const PrioritySelect: React.FC<PrioritySelectProps> = ({ priority = 'NONE', onChange }) => {
  const current = PRIORITY_OPTIONS.find((option) => option.value === priority) || PRIORITY_OPTIONS[0];

  return (
    <div className="relative group min-w-[140px] xl:min-w-[170px]" onClick={(event) => event.stopPropagation()}>
      <div className={`relative flex items-center gap-1.5 xl:gap-2 px-2 xl:px-3 py-1 xl:py-1.5 rounded-lg text-[10px] xl:text-xs border transition-all cursor-pointer hover:shadow-md priority-badge ${current.style}`}>
        <span className="flex-shrink-0">{current.icon}</span>
        <select
          value={priority}
          onChange={(event) => onChange(event.target.value as TenderPriority)}
          className="appearance-none bg-transparent border-none outline-none text-inherit font-inherit cursor-pointer w-full pr-5 xl:pr-6 z-10 focus:ring-0 font-medium"
          aria-label="Alterar prioridade"
        >
          {PRIORITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value} className="bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark">
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="w-2.5 h-2.5 xl:w-3 xl:h-3 absolute right-1.5 xl:right-2 pointer-events-none opacity-50 group-hover:opacity-100" />
      </div>
    </div>
  );
};

const priorityLabel = (priority?: TenderPriority): string =>
  PRIORITY_OPTIONS.find((option) => option.value === priority)?.label || 'Sem prioridade';

interface TenderListProps {
  isOngoingView?: boolean;
  viewMode?: 'PIPELINE' | 'ONGOING' | 'MARKET' | 'CONSULTS' | 'CONCORRENTE';
}

// Helper: converter chave do enum do backend para valor do frontend
const mapBackendStatusToFrontend = (backendStatus: string): TenderStatus => {
  const statusMap: Record<string, TenderStatus> = {
    'OPEN': TenderStatus.OPEN,
    'CLOSED': TenderStatus.CLOSED,
    'ANALYSIS': TenderStatus.ANALYSIS,
    'WON': TenderStatus.WON,
    'LOST': TenderStatus.LOST,
    'PARTICIPATE': TenderStatus.PARTICIPATE,
    'HIGH_PRIORITY': TenderStatus.HIGH_PRIORITY,
    'EXTREME_PRIORITY': TenderStatus.EXTREME_PRIORITY,
    'NO_GO': TenderStatus.NO_GO,
    'ELECTRONIC_QUOTE': TenderStatus.ELECTRONIC_QUOTE,
    'DIRECT_WAIVER': TenderStatus.DIRECT_WAIVER,
    'RANDOM': TenderStatus.RANDOM,
  };
  return statusMap[backendStatus] || TenderStatus.ANALYSIS;
};

const TenderList: React.FC<TenderListProps> = ({ isOngoingView = false, viewMode: initialViewMode }) => {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar dados da API
  useEffect(() => {
    const loadTenders = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getTenders();

        // Converter status do backend (chaves) para frontend (valores)
        const mappedData = data.map(tender => ({
          ...tender,
          status: mapBackendStatusToFrontend(tender.status as any)
        }));

        // Clean up any legacy localStorage key if present (we no longer persist tenders client-side).
        try {
          if (typeof localStorage !== 'undefined' && localStorage.getItem('licitaflow_tenders')) {
            localStorage.removeItem('licitaflow_tenders');
            console.info('Removed legacy localStorage key: licitaflow_tenders');
          }
        } catch (e) { /* ignore localStorage errors */ }

        setTenders(mappedData);
      } catch (error) {
        console.error("Erro ao carregar licitações:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTenders();
  }, []);

  // Controle de Abas (Pipeline, Em Andamento, Mercado, Consultas)
  const [viewMode, setViewMode] = useState<'PIPELINE' | 'ONGOING' | 'MARKET' | 'CONSULTS' | 'CONCORRENTE'>(initialViewMode || 'PIPELINE');
  // Estado para a guia concorrente (subconsultas e itens customizáveis)
  const [concorrenteQueries, setConcorrenteQueries] = useState<string[]>([]);
  const [newConcorrenteName, setNewConcorrenteName] = useState('');
  const [selectedConcorrenteIndex, setSelectedConcorrenteIndex] = useState<number | null>(null);

  // Configuração de Emails
  const [emailRecipients, setEmailRecipients] = useState<string>('equipe@empresa.com');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [tempEmailInput, setTempEmailInput] = useState('');

  // Configuração de Agendamento
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [schedulingTender, setSchedulingTender] = useState<Tender | null>(null);
  const [scheduleForm, setScheduleForm] = useState({ action: '', date: '', time: '' });

  // Configuração de Nova Licitação (Manual)
  const [isNewTenderModalOpen, setIsNewTenderModalOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [newTenderAttachments, setNewTenderAttachments] = useState<Array<{ name: string; url: string; type?: string; size?: number; sourceExternalId?: string }>>([]);
  const [newTenderForm, setNewTenderForm] = useState<Partial<Tender>>({
      title: '',
      organ: '',
      portal: '',
      state: '',
      deadline: '',
      publicationDate: new Date().toISOString().split('T')[0],
      value: 0,
      description: '',
      status: TenderStatus.ANALYSIS
  });

  // --- FILTROS & ORDENAÇÃO ---
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<TenderStatus | 'ALL'>('ALL'); // Status
  const [stateFilter, setStateFilter] = useState<string>('ALL'); // UF
  const [urgencyFilter, setUrgencyFilter] = useState<'ALL' | 'EXPIRED' | 'URGENT' | 'WARNING'>('ALL'); // Novo Filtro de Urgência
  const [sortBy, setSortBy] = useState<'PRIORITY' | 'DEADLINE' | 'PUBLICATION_DATE' | 'ESTIMATED_VALUE'>('PRIORITY');

  // Filtros Avançados
  const [deadlineStart, setDeadlineStart] = useState(''); // Data Prazo Inicio
  const [deadlineEnd, setDeadlineEnd] = useState(''); // Data Prazo Fim
  const [pubDateStart, setPubDateStart] = useState(''); // Data Publicação Inicio
  const [pubDateEnd, setPubDateEnd] = useState(''); // Data Publicação Fim
  const [minValue, setMinValue] = useState(''); // Valor Mínimo
  const [maxValue, setMaxValue] = useState(''); // Valor Máximo

  // Handler para limpar todos os filtros e busca
  const clearFilters = () => {
    setSearchTerm('');
    setFilter('ALL');
    setStateFilter('ALL');
    setUrgencyFilter('ALL');
    setSortBy('PRIORITY');
    setDeadlineStart('');
    setDeadlineEnd('');
    setPubDateStart('');
    setPubDateEnd('');
    setMinValue('');
    setMaxValue('');
    setQuickPrazo('ALL');
    setQuickPrioridade('ALL');
    setItemsPerPage(100);
    setCurrentPage(1);
    // limpa query params para evitar reabrir modais acidentalmente
    navigate('/tenders', { replace: true });
  };

  const handleAdditionalFilesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    // Only accept PDFs for these additional attachments
    const invalid = files.find(f => f.type !== 'application/pdf');
    if (invalid) {
      await showAlert('Por favor, envie apenas arquivos PDF nos anexos adicionais.');
      e.target.value = '';
      return;
    }

    setIsUploadingAttachments(true);
    try {
      for (const file of files) {
        // Gerar chave única para S3
        const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const key = `tenders/${Date.now()}-${Math.random().toString(36).slice(2,8)}-${safeName}`;

        const presign = await apiService.getPresignedUploadUrl(key, file.type || 'application/pdf');
        await apiService.uploadFileToS3(presign.uploadUrl, file);

        // Armazenar referência temporária (será persistida no DB após criar a licitação)
        setNewTenderAttachments(prev => [...prev, { name: file.name, url: presign.key || key, type: file.type, size: file.size }]);
      }
      await showAlert('Anexos carregados com sucesso. Eles serão vinculados à licitação ao salvar.');
    } catch (err) {
      console.error('Erro ao enviar anexos adicionais:', err);
      await showAlert('Falha ao enviar anexos adicionais. Tente novamente.');
    } finally {
      setIsUploadingAttachments(false);
      // reset input to allow reupload
      e.target.value = '';
    }
  };

  const handleRemoveNewAttachment = (index: number) => {
    setNewTenderAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const [notificationState, setNotificationState] = useState<{
    message: string;
    type: 'sending' | 'success';
    // optional undo action shown in toast
    undo?: () => Promise<void>;
  } | null>(null);

  // ID da licitação cujo menu de ações (3 pontos) está aberto
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);
  // âncora para o menu (coordenação para evitar corte por overflow de containers)
  const [actionsAnchorRect, setActionsAnchorRect] = useState<{ top: number; left: number } | null>(null);
  const [deletedTenderTemp, setDeletedTenderTemp] = useState<Tender | null>(null);
  // Contador de consultas externas (mock / Mongo) — exibido na aba 'CONSULTS'
  const [externalCount, setExternalCount] = useState<number | null>(null);
  useEffect(() => {
    const fetchExternalCount = async () => {
      try {
        const apiUrl = '/api/tenders/external';
        const res = await fetch(apiUrl);

        if (!res.ok) {
          console.warn('Falha ao carregar contagem de consultas externas', res.status);
          setExternalCount(0);
          return;
        }

        const json = await res.json();
        setExternalCount(Array.isArray(json) ? json.length : 0);
      } catch (err) {
        console.error('Erro ao buscar contagem de consultas externas:', err);
        setExternalCount(0);
      }
    };
    fetchExternalCount();
  }, []);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailTender, setDetailTender] = useState<Tender | null>(null);
  // Attachments modal
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [attachmentsTender, setAttachmentsTender] = useState<Tender | null>(null);
  const [tenderAttachments, setTenderAttachments] = useState<Array<{ id: string; name: string; url: string; type?: string; size?: number; uploadedAt?: string }>>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isUploadingInAttachmentsModal, setIsUploadingInAttachmentsModal] = useState(false);
  const { showConfirm, showAlert } = useModal();
  const location = useLocation();
  const navigate = useNavigate();

  // Abre modal de detalhe quando existe query param `openTender` (vindo da Agenda)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let openTenderId = params.get('openTender');
    if (!openTenderId) return;

    // Se veio um id com prefixo do tipo 'tender-next-<id>' ou 'tender-deadline-<id>',
    // extrai o id real para evitar chamadas inválidas à API.
    const match = openTenderId.match(/^tender-(?:next|deadline)-(.+)$/);
    if (match) openTenderId = match[1];

    // Se a agenda passou um filtro de objeto/título, aplica no campo de busca para
    // filtrar a listagem e facilitar localização do item.
    const filterObject = params.get('filterObject');
    if (filterObject) {
      setSearchTerm(filterObject);
    }

    (async () => {
      try {
        setIsLoading(true);
        // Buscar diretamente o tender pelo endpoint mais eficiente
        const t = await apiService.getTenderById(openTenderId);
        if (t) {
          const mapped = { ...t, status: mapBackendStatusToFrontend(t.status as any) };
          setDetailTender(mapped);
          setIsDetailModalOpen(true);
        } else {
          console.warn('Tender not found for openTender id', openTenderId);
        }
      } catch (err) {
        console.error('Erro ao buscar licitação por id (openTender):', err);
      } finally {
        setIsLoading(false);
        // limpar query param para não reabrir ao navegar
        navigate('/tenders', { replace: true });
      }
    })();
  }, [location.search]);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  useEffect(() => {
    setViewMode(isOngoingView ? 'ONGOING' : (initialViewMode || 'PIPELINE'));
    setCurrentPage(1);
  }, [location.pathname, initialViewMode, isOngoingView]);

  // Visual-only: modo de renderização (Lista / Cards / Kanban).
  // Estado local, não persiste em URL, não afeta filtros/paginação/handlers.
  const [renderMode, setRenderMode] = useState<'list' | 'cards' | 'kanban'>('list');

  // Chips rápidos aplicados diretamente sobre as datas e prioridades persistidas.
  const [quickPrazo, setQuickPrazo] = useState<'ALL' | 'HOJE' | '7D' | '30D'>('ALL');
  const [quickPrioridade, setQuickPrioridade] = useState<'ALL' | 'CRITICA' | 'ALTA' | 'MEDIA' | 'BAIXA'>('ALL');

  // Status Definitions
  const pipelineStatuses = [
      TenderStatus.EXTREME_PRIORITY,
      TenderStatus.HIGH_PRIORITY,
      TenderStatus.PARTICIPATE,
      TenderStatus.WON,
      TenderStatus.ANALYSIS,
      TenderStatus.LOST,
      TenderStatus.NO_GO
  ];

  const ongoingStatuses = [
      TenderStatus.WON,
      TenderStatus.HIGH_PRIORITY,
      TenderStatus.EXTREME_PRIORITY
  ];

  const marketStatuses = [
      TenderStatus.ELECTRONIC_QUOTE,
      TenderStatus.DIRECT_WAIVER,
      TenderStatus.RANDOM,
      TenderStatus.OPEN,
      TenderStatus.CLOSED
  ];

  // Se estiver no modo "Em Andamento" (rota específica), forçamos o modo ONGOING
  useEffect(() => {
      if (isOngoingView) {
          setViewMode('ONGOING');
      }
  }, [isOngoingView]);

  // Visual-only: sempre inicia em Lista ao montar o componente (entrada na tela).
  useEffect(() => {
    setRenderMode('list');
  }, []);

  // Visual-only: ao trocar de aba, volta sempre pra Lista (modo padrão global).
  // Usuário pode alternar manualmente para Cards/Kanban via switcher da toolbar.
  useEffect(() => {
    setRenderMode('list');
  }, [viewMode]);

  useEffect(() => {
    settingsService.getAppSettings().then(settings => {
      if (settings.emailRecipients) {
        setEmailRecipients(settings.emailRecipients);
      }
    });
  }, []);



  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, sortBy, itemsPerPage, stateFilter, viewMode, deadlineStart, deadlineEnd, pubDateStart, pubDateEnd, minValue, maxValue, urgencyFilter, quickPrazo, quickPrioridade]);

  // Fecha o menu de ações quando clicar fora
  useEffect(() => {
      const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      // se clicar em botão/menu não fecha
      if (target.closest('.actions-menu') || target.closest('.actions-button')) return;
      if (openActionsId) {
        setOpenActionsId(null);
        setActionsAnchorRect(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openActionsId]);

  useEffect(() => {
    if (notificationState?.type === 'success') {
      const timer = setTimeout(() => setNotificationState(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notificationState]);

  const handleSaveEmails = async () => {
    setEmailRecipients(tempEmailInput);
    try {
      const currentSettings = await settingsService.getAppSettings();
      await settingsService.saveAppSettings({
        ...currentSettings,
        emailRecipients: tempEmailInput
      });
    } catch (error) {
      console.error("Failed to save email settings", error);
    }
    setIsEmailModalOpen(false);
  };

  const handleOpenEmailModal = () => {
    setTempEmailInput(emailRecipients);
    setIsEmailModalOpen(true);
  };

  const handleTenderFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;
    if (file.type !== 'application/pdf') {
      await showAlert('Por favor, envie apenas arquivos PDF.');
      return;
    }

    setIsExtracting(true);

    try {
      // Chama a API para extrair dados do PDF de licitação
      const extracted = await apiService.extractTenderFromPDF(file);

      // Função helper para converter DD/MM/AAAA -> YYYY-MM-DD
      const convertDateToISO = (dateStr: string): string => {
        if (!dateStr || dateStr === 'NÃO CONSTA') return '';
        // Tentar formato DD/MM/AAAA
        const ddmmyyyyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (ddmmyyyyMatch) {
          const [, day, month, year] = ddmmyyyyMatch;
          return `${year}-${month}-${day}`;
        }
        // Tentar formato YYYY-MM-DD (já correto)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // Fallback: tentar parse direto
        try {
          const parsed = new Date(dateStr);
          if (!isNaN(parsed.getTime())) {
            return parsed.toISOString().split('T')[0];
          }
        } catch (e) {/* ignore */}
        return '';
      };

      // Converter valor para numérico se possível
      let parsedValue = 0;
      if (extracted.value_numeric != null) {
        parsedValue = extracted.value_numeric;
      } else if (extracted.value && extracted.value !== 'NÃO CONSTA') {
        // Tentar extrair número de string como "R$ 1.234,56" usando parseBRL
        const parsed = parseBRL(extracted.value);
        if (parsed !== null) parsedValue = parsed;
      }

      setNewTenderForm(prev => ({
        ...prev,
        title: (extracted.title && extracted.title !== 'NÃO CONSTA') ? extracted.title : (prev.title || file.name.replace('.pdf','').replace(/[-_]/g,' ')),
        organ: (extracted.agency && extracted.agency !== 'NÃO CONSTA') ? extracted.agency : prev.organ,
        portal: (extracted.portal && extracted.portal !== 'NÃO CONSTA') ? extracted.portal : prev.portal,
        state: (extracted.state && extracted.state !== 'NÃO CONSTA') ? extracted.state : prev.state,
        publicationDate: convertDateToISO(extracted.publication_date) || prev.publicationDate,
        deadline: convertDateToISO(extracted.deadline_date) || prev.deadline,
        value: parsedValue || prev.value,
        description: (extracted.description && extracted.description !== 'NÃO CONSTA') ? extracted.description : prev.description,
        status: (extracted.status_suggestion && extracted.status_suggestion !== 'NÃO CONSTA' && (Object.values(TenderStatus) as string[]).includes(extracted.status_suggestion)) ? (extracted.status_suggestion as TenderStatus) : prev.status,
        fileUrl: extracted.fileUrl || prev.fileUrl // Salvar o caminho do PDF
      }));

      await showAlert('Dados extraídos com sucesso! Verifique os campos preenchidos.');
      console.log('Evidências extração licitação:', extracted);
      logActivity('LICITACAO_EXTRAIDA_IA', `Licitação extraída via IA: ${extracted.title || ''}`, 'SUCCESS');

      setIsExtracting(false);
      // Resetar input para permitir reupload do mesmo arquivo
      e.target.value = '';
    } catch (err) {
      console.error('Erro ao extrair licitação do PDF:', err);
      setIsExtracting(false);
      await showAlert('Não foi possível extrair dados do PDF. Verifique o arquivo ou preencha manualmente.');
      e.target.value = '';
    }
  };

  const handleOpenScheduleModal = (tender: Tender) => {
    setSchedulingTender(tender);
    setScheduleForm({
        action: tender.nextAction || 'Acompanhar Licitação',
        date: tender.nextActionDate || tender.deadline, // Padrão é data do prazo
        time: tender.nextActionTime || '09:00'
    });
    setIsScheduleModalOpen(true);
  };

  // Handler when adding an external tender to the wallet
  const handleAddExternalTender = (external: ExternalTender) => {
    // Helper to convert dd/MM/yyyy or ISO-ish to YYYY-MM-DD
    const toISODate = (raw?: string) => {
      if (!raw) return '';
      // dd/MM/yyyy
      const ddmmyyyy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
      // ISO like 2026-02-16
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.split('T')[0];
      // try Date parse
      const p = Date.parse(raw);
      if (!Number.isNaN(p)) return new Date(p).toISOString().split('T')[0];
      return '';
    };

    // Map external tender into newTenderForm
    setNewTenderForm({
      title: external.objeto || external.edital || external.urlEdital || '',
      organ: external.orgao || '',
      portal: external.portal || external.fonte || '',
      state: external.estado || '',
      publicationDate: toISODate(external.incluido || external.atualizado),
      deadline: toISODate(external.encerramento),
      value: typeof external.valorTotal === 'number' ? external.valorTotal : 0,
      description: external.observacoes || '',
      status: TenderStatus.ANALYSIS,
    });

    // Map attachments (preserve external URLs)
    const mapped = (external.attachments || []).map((a: any) => ({
      name: a.nome || a.name || a.title || 'Anexo',
      url: a.url || a.link || '',
      type: undefined,
      size: undefined,
      sourceExternalId: external.id,
    })).filter((x: any) => x.url);

    setNewTenderAttachments(mapped);
    setIsNewTenderModalOpen(true);
  };

  const isExternalDocumentUrl = (value?: string | null) => /^https?:\/\//i.test(String(value || '').trim());

  const buildDocumentOpenUrl = (rawUrlOrKey: string, tenderId?: string) => {
    const normalized = String(rawUrlOrKey || '').trim();
    const isAbsolute = isExternalDocumentUrl(normalized);
    const hostBase = window.location.hostname === 'localhost' ? 'http://localhost:3005' : `http://${window.location.hostname}:3005`;
    if (isAbsolute) {
      // Portais de compras dependem da sessão do navegador. Abrir diretamente
      // preserva a autenticação do usuário e evita tratar uma URL como upload.
      return normalized;
    }
    // Otherwise assume it's an S3/local key and use s3 proxy or uploads view when appropriate
    // If key looks like a local uploads key (starts with uploads/), use uploads view
    if (normalized.startsWith('uploads/') || normalized.includes('/uploads/')) {
      const key = normalized.replace(/^uploads\//, '');
      const q = tenderId ? `?tenderId=${encodeURIComponent(tenderId)}` : '';
      return `${hostBase}/api/uploads/${encodeURIComponent(key)}/view${q}`;
    }
    return `${hostBase}/api/s3/proxy?key=${encodeURIComponent(normalized)}`;
  };

  // --- Attachments modal handlers ---
  const openAttachmentsModal = async (tender: Tender) => {
    setAttachmentsTender(tender);
    setIsAttachmentsModalOpen(true);
    setIsLoadingAttachments(true);
    try {
      const list = await apiService.getTenderAttachments(tender.id);
      setTenderAttachments(list || []);
    } catch (err) {
      console.error('Erro ao carregar anexos da licitação:', err);
      await showAlert('Falha ao carregar anexos.');
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!attachmentsTender) return;
    const confirmed = await showConfirm('Confirma exclusão deste anexo?', 'Remover anexo');
    if (!confirmed) return;
    try {
      await apiService.deleteTenderAttachment(attachmentsTender.id, attachmentId);
      setTenderAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Erro ao deletar anexo:', err);
      await showAlert('Falha ao deletar anexo.');
    }
  };

  const handleAttachmentsModalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files as FileList) : [];
    if (files.length === 0 || !attachmentsTender) return;

    const invalid = files.find(f => f.type !== 'application/pdf');
    if (invalid) {
      await showAlert('Por favor, envie apenas arquivos PDF nos anexos.');
      e.target.value = '';
      return;
    }

    setIsUploadingInAttachmentsModal(true);
    try {
      for (const file of files) {
  const safeName = (file as File).name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const key = `tenders/${Date.now()}-${Math.random().toString(36).slice(2,8)}-${safeName}`;
  const presign = await apiService.getPresignedUploadUrl(key, (file as File).type || 'application/pdf');
  await apiService.uploadFileToS3(presign.uploadUrl, file as File);

        const created = await apiService.createTenderAttachment(attachmentsTender.id, {
          name: (file as File).name,
          url: presign.key || key,
          type: (file as File).type,
          size: (file as File).size
        });
        setTenderAttachments(prev => [...prev, created]);
      }
    } catch (err) {
      console.error('Erro ao enviar anexos:', err);
      await showAlert('Falha ao enviar anexos.');
    } finally {
      setIsUploadingInAttachmentsModal(false);
      e.target.value = '';
    }
  };

  const handleSaveSchedule = async () => {
    if (!schedulingTender) return;

    try {
      const updatedTender = await apiService.updateTender(schedulingTender.id, {
        nextAction: scheduleForm.action,
        nextActionDate: scheduleForm.date,
        nextActionTime: scheduleForm.time
      });

      // Converter status do backend
      const mappedTender = {
        ...updatedTender,
        status: mapBackendStatusToFrontend(updatedTender.status as any)
      };

      setTenders(tenders.map(t => t.id === schedulingTender.id ? mappedTender : t));
      logActivity('AGENDAMENTO', `Ação "${scheduleForm.action}" agendada para ${scheduleForm.date} às ${scheduleForm.time} (Licitação ID: ${schedulingTender.id})`, 'SUCCESS');
      setIsScheduleModalOpen(false);
      setNotificationState({ message: 'Agendado com sucesso na Agenda!', type: 'success' });
    } catch (error) {
      alert("Erro ao salvar agendamento no banco.");
    }
  };

  const handleSaveNewTender = async () => {
      if (!newTenderForm.title || !newTenderForm.organ || !newTenderForm.deadline) {
          alert("Os campos Título, Órgão e Prazo são obrigatórios.");
          return;
      }

      try {
        const newTender = await apiService.createTender({
          ...newTenderForm,
          isRegistered: false,
          areDocsReady: false
        });

        // Converter status do backend
        const mappedTender = {
          ...newTender,
          status: mapBackendStatusToFrontend(newTender.status as any)
        };

        setTenders([mappedTender, ...tenders]);
        setIsNewTenderModalOpen(false);
        logActivity('CRIACAO_MANUAL', `Licitação criada manualmente: ${newTender.title}`, 'SUCCESS');

        // Persistir anexos adicionais (se houver)
        if (newTenderAttachments.length > 0) {
          try {
            for (const att of newTenderAttachments) {
              await apiService.createTenderAttachment(newTender.id, {
                name: att.name,
                url: att.url,
                type: att.type,
                size: att.size,
              });
            }
          } catch (err) {
            console.error('Erro ao persistir anexos da licitação:', err);
            // Não bloquear a criação da licitação, apenas notificar
            setNotificationState({ message: 'Licitação criada, mas falha ao vincular anexos.', type: 'sending' });
          }
        }

        // Reset form
        setNewTenderForm({
            title: '', organ: '', portal: '', state: '', deadline: '', publicationDate: new Date().toISOString().split('T')[0], value: 0, description: '', status: TenderStatus.ANALYSIS
        });
        setNewTenderAttachments([]);
      } catch (error: any) {
        alert(error?.message || "Erro ao criar licitação no banco.");
      }
  };

  const handleStatusChange = async (id: string, newStatus: TenderStatus) => {
    const tender = tenders.find(t => t.id === id);
    if (!tender) return;

    try {
      // Envia o valor do enum frontend (ex: 'Ganha'); apiService converte para a chave backend
      const updatedTender = await apiService.updateTender(id, { status: newStatus });
      const mappedTender = {
        ...updatedTender,
        status: mapBackendStatusToFrontend(updatedTender.status as any)
      };
      setTenders(prev => prev.map(t => t.id === id ? mappedTender : t));

      logActivity(
          'ALTERACAO_STATUS',
          `Status alterado para "${newStatus}" na licitação "${tender.title}" (ID: ${id})`,
          'INFO'
      );

      if ([TenderStatus.PARTICIPATE, TenderStatus.HIGH_PRIORITY, TenderStatus.EXTREME_PRIORITY].includes(newStatus)) {
          setNotificationState({ message: `Preparando notificação para equipe (${newStatus})...`, type: 'sending' });
          const emailSent = await sendEmailNotification(tender, newStatus, emailRecipients);
          if (emailSent) {
              setNotificationState({ message: `Alerta enviado para: ${emailRecipients}`, type: 'success' });
          } else {
              setNotificationState(null);
          }
      }
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      const errorMessage = error?.message || error?.toString() || "Erro desconhecido";
      await showAlert(`Erro ao atualizar status: ${errorMessage}`);
    }
  };

  const handlePriorityChange = async (id: string, priority: TenderPriority) => {
    const tender = tenders.find((item) => item.id === id);
    if (!tender) return;

    try {
      const updatedTender = await apiService.updateTender(id, { priority });
      const mappedTender = {
        ...updatedTender,
        status: mapBackendStatusToFrontend(updatedTender.status as any),
      };
      setTenders((current) => current.map((item) => item.id === id ? mappedTender : item));
      logActivity('ALTERACAO_PRIORIDADE', `Prioridade alterada para "${priorityLabel(priority)}" na licitação "${tender.title}" (ID: ${id})`, 'INFO');
    } catch (error: any) {
      console.error('Erro ao atualizar prioridade:', error);
      await showAlert(`Erro ao atualizar prioridade: ${error?.message || 'erro desconhecido'}`);
    }
  };

  const handleToggleChecklist = async (id: string, field: 'isRegistered' | 'areDocsReady') => {
      const tender = tenders.find(t => t.id === id);
      if (!tender) return;

      const newValue = !tender[field];

      try {
        const updatedTender = await apiService.updateTender(id, { [field]: newValue });
        const mappedTender = {
          ...updatedTender,
          status: mapBackendStatusToFrontend(updatedTender.status as any)
        };
        setTenders(tenders.map(t => t.id === id ? mappedTender : t));

        const fieldName = field === 'isRegistered' ? 'Cadastro no Portal' : 'Documentos Prontos';
        logActivity(
            'CHECKLIST_ATUALIZACAO',
            `Checklist "${fieldName}" marcado como ${newValue ? 'CONCLUÍDO' : 'PENDENTE'} na licitação ID: ${id}`,
            'INFO'
        );
      } catch (error) {
        alert("Erro ao atualizar checklist no banco.");
      }
  };

  // Excluir licitação
  const handleDeleteTender = async (id: string) => {
    const tender = tenders.find(t => t.id === id);
    if (!tender) return;

    // Usar modal global de confirmação (sem alert)
    const confirmed = await showConfirm(`Confirma exclusão da licitação "${tender.title}"? Esta ação é irreversível.`,
      'Confirmar exclusão');
    if (!confirmed) return;

    try {
      // Executa exclusão no backend
      await apiService.deleteTender(id);

      // Guarda temporariamente para permitir undo
      setDeletedTenderTemp(tender);

      // Remove do estado (UI)
      setTenders(prev => prev.filter(t => t.id !== id));
      setOpenActionsId(null);
      logActivity('DELETAR_LICITACAO', `Licitação deletada: ${tender.title} (ID: ${id})`, 'SUCCESS');

      // Configura toast com undo
      setNotificationState({
        message: 'Licitação excluída.',
        type: 'success',
        undo: async () => {
          if (!tender) return;
          try {
            // Recriar licitação (undo rápido) - o backend gerará novo ID
            const recreated = await apiService.createTender({
              title: tender.title,
              organ: tender.organ,
              portal: tender.portal,
              state: tender.state,
              deadline: tender.deadline,
              publicationDate: tender.publicationDate,
              value: tender.value,
              description: tender.description,
              status: tender.status,
              isRegistered: tender.isRegistered,
              areDocsReady: tender.areDocsReady,
              nextAction: tender.nextAction,
              nextActionDate: tender.nextActionDate,
              nextActionTime: tender.nextActionTime,
            });

            const mapped = {
              ...recreated,
              status: mapBackendStatusToFrontend(recreated.status as any)
            } as Tender;

            setTenders(prev => [mapped, ...prev]);
            setDeletedTenderTemp(null);
            setNotificationState({ message: 'Restauração realizada.', type: 'success' });
            logActivity('UNDO_DELETE', `Restauração de licitação: ${tender.title}`, 'SUCCESS');
          } catch (e: any) {
            await showAlert(e?.message || 'Erro ao restaurar licitação.');
          }
        }
      });

    } catch (error: any) {
      await showAlert(error?.message || 'Erro ao excluir licitação no banco.');
    }
  };

  const clearAdvancedFilters = () => {
      setDeadlineStart('');
      setDeadlineEnd('');
      setPubDateStart('');
      setPubDateEnd('');
      setMinValue('');
      setMaxValue('');
      setUrgencyFilter('ALL');
  };

  const filteredTenders = tenders.filter(tender => {
    // 1. Filtragem por Modo de Visualização (Abas)
    let allowedStatuses: TenderStatus[] = [];

  if (isOngoingView || viewMode === 'ONGOING') {
    allowedStatuses = ongoingStatuses;
  } else if (viewMode === 'PIPELINE') {
    allowedStatuses = pipelineStatuses;
  } else if (viewMode === 'CONSULTS') {
    // Consultas: mostrar todas as licitações (ignorar segmentação por status)
    allowedStatuses = Object.values(TenderStatus);
  } else {
    allowedStatuses = marketStatuses;
  }

    if (!allowedStatuses.includes(tender.status)) {
        return false;
    }

    // 2. Filtro de Dropdown (Status Específico)
    const matchesStatus = filter === 'ALL' || tender.status === filter;

    // 3. Filtro de UF e Busca
    const matchesState = stateFilter === 'ALL' || tender.state === stateFilter;
    const matchesSearch = tender.organ.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tender.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (tender.portal && tender.portal.toLowerCase().includes(searchTerm.toLowerCase()));

    const deadlineDate = parseCalendarDate(tender.deadline);
    const deadlineStartDate = parseCalendarDate(deadlineStart);
    const deadlineEndDate = parseCalendarDate(deadlineEnd);

    // 4. Filtro de Intervalo de Datas (Prazo), comparando somente o dia do calendário.
    let matchesDeadline = true;
    if (deadlineStartDate) {
        matchesDeadline = Boolean(deadlineDate) && deadlineDate!.getTime() >= deadlineStartDate.getTime();
    }
    if (matchesDeadline && deadlineEndDate) {
        matchesDeadline = Boolean(deadlineDate) && deadlineDate!.getTime() <= deadlineEndDate.getTime();
    }

    // 5. Filtro de Data de Publicação
    let matchesPubDate = true;
    if (tender.publicationDate) {
        if (pubDateStart) {
            matchesPubDate = matchesPubDate && new Date(tender.publicationDate) >= new Date(pubDateStart);
        }
        if (pubDateEnd) {
            matchesPubDate = matchesPubDate && new Date(tender.publicationDate) <= new Date(pubDateEnd);
        }
    } else if (pubDateStart || pubDateEnd) {
        // Se tem filtro mas não tem data, não mostra
        matchesPubDate = false;
    }

    // 6. Filtro de Valor Estimado
    let matchesValue = true;
    if (minValue) {
        matchesValue = matchesValue && tender.value >= parseFloat(minValue);
    }
    if (maxValue) {
        matchesValue = matchesValue && tender.value <= parseFloat(maxValue);
    }

    const daysToDeadline = daysUntilCalendarDate(tender.deadline);

    // 7. Filtro de Urgência
    let matchesUrgency = true;
    if (urgencyFilter !== 'ALL') {
        if (urgencyFilter === 'EXPIRED') matchesUrgency = daysToDeadline !== null && daysToDeadline < 0;
        else if (urgencyFilter === 'URGENT') matchesUrgency = daysToDeadline !== null && daysToDeadline >= 0 && daysToDeadline <= 7;
        else if (urgencyFilter === 'WARNING') matchesUrgency = daysToDeadline !== null && daysToDeadline > 7 && daysToDeadline <= 15;
    }

    // 8. Filtros rápidos de prazo: Hoje, próximos 7 dias e próximos 30 dias.
    const matchesQuickDeadline = quickPrazo === 'ALL'
      || (quickPrazo === 'HOJE' && daysToDeadline === 0)
      || (quickPrazo === '7D' && daysToDeadline !== null && daysToDeadline >= 0 && daysToDeadline <= 7)
      || (quickPrazo === '30D' && daysToDeadline !== null && daysToDeadline >= 0 && daysToDeadline <= 30);

    // 9. Prioridade é independente do status operacional da licitação.
    const matchesQuickPriority = quickPrioridade === 'ALL'
      || (quickPrioridade === 'CRITICA' && tender.priority === 'ULTRA')
      || (quickPrioridade === 'ALTA' && tender.priority === 'VERY_HIGH')
      || (quickPrioridade === 'MEDIA' && tender.priority === 'PRIORITY')
      || (quickPrioridade === 'BAIXA' && (!tender.priority || tender.priority === 'NONE'));

    return matchesStatus && matchesSearch && matchesState && matchesDeadline && matchesPubDate && matchesValue && matchesUrgency && matchesQuickDeadline && matchesQuickPriority;
  });

  const sortedTenders = [...filteredTenders].sort((a, b) => {
    if (sortBy === 'PRIORITY') {
      const priorityScore = (priority?: TenderPriority) => {
        switch (priority) {
          case 'ULTRA': return 5;
          case 'VERY_HIGH': return 4;
          case 'PRIORITY': return 3;
          case 'NONE': return 2;
          case 'NO_GO': return 1;
          default: return 2;
        }
      };
      const scoreDiff = priorityScore(b.priority) - priorityScore(a.priority);
      if (scoreDiff !== 0) return scoreDiff;
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    } else if (sortBy === 'PUBLICATION_DATE') {
        const dateA = a.publicationDate ? new Date(a.publicationDate).getTime() : 0;
        const dateB = b.publicationDate ? new Date(b.publicationDate).getTime() : 0;
        return dateB - dateA;
    } else if (sortBy === 'ESTIMATED_VALUE') {
        return b.value - a.value;
    } else {
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    }
  });

  const totalPages = Math.ceil(sortedTenders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTenders = sortedTenders.slice(startIndex, endIndex);

  const activeAdvancedFilters = [deadlineStart, deadlineEnd, pubDateStart, pubDateEnd, minValue, maxValue].some(Boolean) || urgencyFilter !== 'ALL';
  const hasActiveFilters = Boolean(searchTerm && searchTerm.trim())
    || filter !== 'ALL'
    || stateFilter !== 'ALL'
    || urgencyFilter !== 'ALL'
    || quickPrazo !== 'ALL'
    || quickPrioridade !== 'ALL'
    || activeAdvancedFilters;

  return (
    <>
  <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark overflow-visible flex flex-col min-h-0 relative">

        {/* Toast Notification */}
        {notificationState && (
          <div className="absolute top-4 right-4 z-50 animate-fade-in-up flex flex-col gap-2">
            <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 border transition-all ${
                notificationState.type === 'sending'
                ? 'bg-brand-500 text-white border-blue-700'
                : 'bg-green-600 dark:bg-green-500 text-white border-green-700'
            }`}>
                  <div className={`rounded-full p-1 ${notificationState.type === 'sending' ? 'bg-blue-500 dark:bg-blue-600' : 'bg-white/20'}`}>
                      {notificationState.type === 'sending' ? (
                          <Loader2 className="w-3 h-3 text-white animate-spin" />
                      ) : (
                          <Check className="w-3 h-3 text-white" />
                      )}
                  </div>
                  <div className="flex flex-col">
                      <span className="font-semibold text-xs opacity-90">
                          {notificationState.type === 'sending' ? 'Processando...' : 'Sucesso'}
                      </span>
                      <span className="text-sm font-medium">{notificationState.message}</span>
                  </div>
            </div>
          </div>
        )}

        {/* Navegação por Abas — unificada em brand-500 (estilo terracota) */}
        {!isOngoingView && (
          <div className="border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
            <div className="flex overflow-x-auto scrollbar-thin px-2">
              {([
                { key: 'PIPELINE',    label: 'Minha Carteira',          shortLabel: 'Carteira', Icon: FileText, count: tenders.filter(t => pipelineStatuses.includes(t.status)).length },
                { key: 'ONGOING',     label: 'Em Andamento',            shortLabel: 'Andamento', Icon: Clock,   count: tenders.filter(t => ongoingStatuses.includes(t.status)).length },
                { key: 'CONSULTS',    label: 'Licitações em Participação',  shortLabel: 'Participação', Icon: Sparkles, count: externalCount ?? 0 },
                { key: 'CONCORRENTE', label: 'Concorrente',             shortLabel: 'Concorrente', Icon: Users,  count: concorrenteQueries.length },
                { key: 'MARKET',      label: 'Mercado & Cotações',      shortLabel: 'Mercado',  Icon: ShoppingBag, count: tenders.filter(t => marketStatuses.includes(t.status)).length },
              ] as const).map(tab => {
                const active = viewMode === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => { setViewMode(tab.key); setCurrentPage(1); }}
                    className={[
                      'flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-[13px] font-medium whitespace-nowrap transition-colors relative',
                      active
                        ? 'text-brand-700 dark:text-brand-300 after:content-[""] after:absolute after:left-3 after:right-3 after:bottom-0 after:h-[2px] after:bg-brand-500 after:rounded-full'
                        : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark',
                    ].join(' ')}
                  >
                    <tab.Icon className={`w-3.5 h-3.5 ${active ? 'text-brand-500' : ''}`} />
                    <span className="hidden lg:inline">{tab.label}</span>
                    <span className="lg:hidden">{tab.shortLabel}</span>
                    <span
                      className={[
                        'text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full tabular',
                        active
                          ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300'
                          : 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark',
                      ].join(' ')}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Mensagem de contexto da aba ativa */}
            {viewMode === 'CONSULTS' && (
              <div className="px-6 py-3 text-sm flex items-center gap-2 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 dark:text-teal-200 border-b border-teal-100 dark:border-teal-800/50">
                <Search className="w-4 h-4" />
                <span>Explore e pesquise <strong>todas as licitações</strong> disponíveis no sistema</span>
              </div>
            )}
            {viewMode === 'CONCORRENTE' && (
              <div className="px-6 py-3 text-sm flex items-center gap-2 bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-200 dark:text-fuchsia-200 border-b border-fuchsia-100 dark:border-fuchsia-800/50">
                <Users className="w-4 h-4" />
                <span>Monitore <strong>licitações ganhas por concorrentes</strong> e oportunidades de mercado</span>
              </div>
            )}
          </div>
        )}

        {/* Header com Título e Ações */}
        <div className="p-6 border-b border-line dark:border-line-dark flex flex-col gap-4 flex-shrink-0">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark">
                {viewMode === 'CONSULTS'
                  ? 'Consulta de Licitações'
                  : viewMode === 'CONCORRENTE'
                  ? 'Análise de Concorrentes'
                  : viewMode === 'ONGOING'
                  ? 'Licitações em Andamento'
                  : viewMode === 'MARKET'
                  ? 'Mercado & Cotações'
                  : 'Minha Carteira'}
              </h2>
              <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                {viewMode === 'CONSULTS'
                  ? 'Pesquise e explore todas as licitações em participação no sistema.'
                  : viewMode === 'CONCORRENTE'
                  ? 'Acompanhe as vitórias dos concorrentes e identifique padrões de mercado.'
                  : viewMode === 'ONGOING'
                  ? 'Acompanhe apenas licitações ganhas ou prioritárias.'
                  : viewMode === 'MARKET'
                  ? 'Cotações eletrônicas, dispensas e oportunidades de mercado.'
                  : 'Gerencie suas oportunidades, prioridades e pipeline de vendas.'
                }
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsNewTenderModalOpen(true)}
                className={`${theme.classes.button.primary} flex items-center gap-2 px-4 py-2 text-sm`}
              >
                <Plus className="w-4 h-4" />
                Nova Licitação
              </button>
              <button
                onClick={handleOpenEmailModal}
                className={`${theme.classes.button.outline} flex items-center gap-2 px-3 py-2 text-sm`}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Alertas</span>
              </button>
            </div>
          </div>

          {/* KPI Strip — 4 tiles usando dados já filtrados (só Minha Carteira e Em Andamento) */}
          {(viewMode === 'PIPELINE' || viewMode === 'ONGOING') && (() => {
            const total = filteredTenders.length;
            const critical = filteredTenders.filter(t => {
              if (!t.deadline) return false;
              const d = daysUntilCalendarDate(t.deadline);
              if (d === null) return false;
              return d >= 0 && d <= 3;
            }).length;
            const docsReady = filteredTenders.filter(t => (t as any).areDocsReady).length;
            const sumValue = filteredTenders.reduce((a, t) => a + toFiniteNumber(t.value), 0);
            const fmtCompactBRL = (v: number) => {
              if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
              if (v >= 1_000) return `R$ ${Math.round(v / 1_000)}K`;
              return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
            };
            const pct = total > 0 ? Math.round((docsReady / total) * 100) : 0;
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Total na carteira</div>
                    <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{total}</div>
                  </div>
                </div>
                <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-danger-soft dark:bg-danger-soft-dark text-danger dark:text-danger-dark flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Prazo crítico (≤3d)</div>
                    <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{critical}</div>
                    <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">requerem ação imediata</div>
                  </div>
                </div>
                <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success-soft dark:bg-success-soft-dark text-success dark:text-success-dark flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Docs prontos</div>
                    <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{docsReady}<span className="text-[14px] font-normal text-ink-subtitle dark:text-ink-subtitle-dark">/{total}</span></div>
                    <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">{pct}% da carteira</div>
                  </div>
                </div>
                <div className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card p-4 shadow-sm-warm dark:shadow-sm-dark flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-info-soft dark:bg-info-soft-dark text-info dark:text-info-dark flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Valor estimado</div>
                    <div className="text-[22px] font-semibold text-ink-title dark:text-ink-title-dark tabular leading-none mt-1">{fmtCompactBRL(sumValue)}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Filtros Bar - REDESIGNED RESPONSIVO */}
          <div className="bg-surface-alt dark:bg-surface-dark-alt/50 p-3 xl:p-4 rounded-xl border border-line dark:border-line-dark">
            {/* Linha Principal de Filtros */}
            <div className="flex flex-col gap-2 xl:gap-3">
              {/* Busca + mode switcher + contador */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-3.5 w-3.5 xl:h-4 xl:w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por órgão, objeto, portal..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-xs xl:text-sm rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block pl-9 xl:pl-10 p-2 xl:p-2.5 outline-none transition-colors"
                  />
                </div>
                {viewMode !== 'CONCORRENTE' && viewMode !== 'CONSULTS' && (
                  <>
                    <span className="hidden md:inline-flex items-center text-[11.5px] text-ink-subtitle dark:text-ink-subtitle-dark whitespace-nowrap tabular px-2">
                      {filteredTenders.length} de {tenders.filter(t => ((isOngoingView || viewMode === 'ONGOING') ? ongoingStatuses : viewMode === 'PIPELINE' ? pipelineStatuses : marketStatuses).includes(t.status)).length}
                    </span>
                    <div className="inline-flex items-center gap-0.5 p-1 rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark flex-shrink-0" role="radiogroup" aria-label="Modo de visualização">
                      {([
                        { k: 'list',   label: 'Lista',  Icon: List },
                        { k: 'cards',  label: 'Cards',  Icon: CheckSquare },
                        { k: 'kanban', label: 'Kanban', Icon: Activity },
                      ] as const).map(o => {
                        const active = renderMode === o.k;
                        return (
                          <button
                            key={o.k}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            title={o.label}
                            onClick={() => setRenderMode(o.k)}
                            className={[
                              'inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-medium transition-colors',
                              active
                                ? 'bg-brand-500 text-white'
                                : 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark',
                            ].join(' ')}
                          >
                            <o.Icon className="w-3 h-3" />
                            <span className="hidden lg:inline">{o.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Chips rápidos de prazo e prioridade persistida. */}
              {viewMode !== 'CONCORRENTE' && viewMode !== 'CONSULTS' && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5">
                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">Prazo</span>
                  {([
                    { k: 'ALL' as const,  label: 'Todos' },
                    { k: 'HOJE' as const, label: 'Hoje' },
                    { k: '7D' as const,   label: 'Próximos 7 dias' },
                    { k: '30D' as const,  label: '30 dias' },
                  ]).map(c => {
                    const active = quickPrazo === c.k;
                    return (
                      <button
                        key={c.k}
                        type="button"
                          onClick={() => setQuickPrazo(active ? 'ALL' : c.k)}
                        className={[
                          'inline-flex items-center h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                          active
                            ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                            : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                        ].join(' ')}
                      >
                        {c.label}
                      </button>
                    );
                  })}

                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark ml-1">Prioridade</span>
                  {([
                    { k: 'CRITICA' as const, label: 'Crítica', dot: 'bg-danger dark:bg-danger-dark' },
                    { k: 'ALTA' as const,    label: 'Alta',    dot: 'bg-warning dark:bg-warning-dark' },
                    { k: 'MEDIA' as const,   label: 'Média',   dot: 'bg-info dark:bg-info-dark' },
                    { k: 'BAIXA' as const,   label: 'Baixa',   dot: 'bg-success dark:bg-success-dark' },
                  ]).map(c => {
                    const active = quickPrioridade === c.k;
                    return (
                      <button
                        key={c.k}
                        type="button"
                        onClick={() => setQuickPrioridade(active ? 'ALL' : c.k)}
                        className={[
                          'inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-[12px] font-medium transition-colors',
                          active
                            ? 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border-brand-200 dark:border-[rgba(203,106,30,0.40)]'
                            : 'bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark border-line dark:border-line-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
                        ].join(' ')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        {c.label}
                      </button>
                    );
                  })}

                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={() => { clearFilters(); setQuickPrazo('ALL'); setQuickPrioridade('ALL'); }}
                      className="ml-auto inline-flex items-center gap-1 h-7 px-3 rounded-full border border-brand-500 text-brand-700 dark:text-brand-300 text-[12px] font-medium hover:bg-brand-50 dark:hover:bg-[rgba(203,106,30,0.15)] transition-colors"
                    >
                      Limpar filtros <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}

              {/* Filtros Dropdown - Grid Responsivo */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                <div className="relative">
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="appearance-none bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-xs xl:text-sm rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full pl-3 pr-7 py-2 xl:py-2.5 cursor-pointer outline-none"
                  >
                    <option value="ALL">UF: Todas</option>
                    {BRAZIL_STATES.map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                  <MapPin className="pointer-events-none absolute inset-y-0 right-2 h-full w-3 xl:w-3.5 text-ink-placeholder dark:text-ink-placeholder-dark my-auto" />
                </div>

                {/* Filtro de Urgência */}
                <div className="relative">
                  <select
                    value={urgencyFilter}
                    onChange={(e) => setUrgencyFilter(e.target.value as any)}
                    className={`appearance-none border text-xs xl:text-sm rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full pl-3 pr-7 py-2 xl:py-2.5 cursor-pointer outline-none font-medium ${
                      urgencyFilter === 'EXPIRED' ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/60 text-red-700 dark:text-red-300' :
                      urgencyFilter === 'URGENT' ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800/60 text-orange-700 dark:text-orange-300' :
                      urgencyFilter === 'WARNING' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-surface dark:bg-surface-dark border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark'
                    }`}
                  >
                    <option value="ALL">Prazo: Todos</option>
                    <option value="URGENT">🚨 Urgente (7d)</option>
                    <option value="WARNING">⚠️ Atenção (15d)</option>
                    <option value="EXPIRED">❌ Vencidos</option>
                  </select>
                  <Hourglass className={`pointer-events-none absolute inset-y-0 right-2 h-full w-3 xl:w-3.5 my-auto ${
                    urgencyFilter !== 'ALL' ? 'text-current' : 'text-ink-placeholder dark:text-ink-placeholder-dark'
                  }`} />
                </div>

                <div className="relative">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as TenderStatus | 'ALL')}
                    className="appearance-none bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-xs xl:text-sm rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full pl-3 pr-7 py-2 xl:py-2.5 cursor-pointer outline-none"
                  >
                    <option value="ALL">Status: Todos</option>
                    {((isOngoingView || viewMode === 'ONGOING') ? ongoingStatuses : (viewMode === 'PIPELINE' ? pipelineStatuses : marketStatuses)).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <Filter className="pointer-events-none absolute inset-y-0 right-2 h-full w-3 xl:w-3.5 text-ink-placeholder dark:text-ink-placeholder-dark my-auto" />
                </div>

                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'PRIORITY' | 'DEADLINE' | 'PUBLICATION_DATE' | 'ESTIMATED_VALUE')}
                    className="appearance-none bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-xs xl:text-sm rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 block w-full pl-3 pr-7 py-2 xl:py-2.5 cursor-pointer outline-none"
                  >
                    <option value="PRIORITY">Ord: Prioridade</option>
                    <option value="DEADLINE">Ord: Prazo</option>
                    <option value="PUBLICATION_DATE">Ord: Publicação</option>
                    <option value="ESTIMATED_VALUE">Ord: Valor</option>
                  </select>
                  <SortAsc className="pointer-events-none absolute inset-y-0 right-2 h-full w-3 xl:w-3.5 text-ink-placeholder dark:text-ink-placeholder-dark my-auto" />
                </div>

                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 xl:py-2.5 rounded-lg border text-xs xl:text-sm font-medium transition-colors ${
                    showAdvancedFilters || activeAdvancedFilters
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/60 text-blue-700 dark:text-blue-300'
                      : 'bg-surface dark:bg-surface-dark border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt'
                  }`}
                >
                  {showAdvancedFilters ? <ChevronUp className="w-3.5 h-3.5" /> : <Filter className="w-3.5 h-3.5" />}
                  <span className="hidden lg:inline">Avançados</span>
                  <span className="lg:hidden">+</span>
                  {activeAdvancedFilters && !showAdvancedFilters && (
                    <span className="flex h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-500"></span>
                  )}
                </button>

                {/* Botão para limpar todos os filtros */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className={`${theme.classes.button.outline} hidden sm:flex items-center gap-2 px-3 py-2 text-sm`}
                    title="Limpar filtros"
                  >
                    <FilterX className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                    <span className="hidden md:inline">Limpar filtros</span>
                  </button>
                )}
              </div>
            </div>

                {/* Filtros Avançados - GRID RESPONSIVO OTIMIZADO */}
                {showAdvancedFilters && (
                    <div className="mt-3 pt-3 border-t border-line dark:border-line-dark grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 xl:gap-4 animate-fade-in-down">

                        {/* Grupo 1: Datas de Prazo */}
                        <div className="bg-surface dark:bg-surface-dark p-3 rounded-lg border border-line dark:border-line-dark shadow-sm">
                            <label className="block text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark mb-2 flex items-center gap-1">
                                <CalendarDays className="w-3 h-3 xl:w-3.5 xl:h-3.5" /> Prazo Entrega
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={deadlineStart}
                                    onChange={(e) => setDeadlineStart(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                                <span className="text-ink-placeholder dark:text-ink-placeholder-dark text-[10px] xl:text-xs flex-shrink-0">até</span>
                                <input
                                    type="date"
                                    value={deadlineEnd}
                                    onChange={(e) => setDeadlineEnd(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Grupo 2: Datas de Publicação */}
                        <div className="bg-surface dark:bg-surface-dark p-3 rounded-lg border border-line dark:border-line-dark shadow-sm">
                            <label className="block text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark mb-2 flex items-center gap-1">
                                <FileText className="w-3 h-3 xl:w-3.5 xl:h-3.5" /> Data Publicação
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={pubDateStart}
                                    onChange={(e) => setPubDateStart(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                                <span className="text-ink-placeholder dark:text-ink-placeholder-dark text-[10px] xl:text-xs flex-shrink-0">até</span>
                                <input
                                    type="date"
                                    value={pubDateEnd}
                                    onChange={(e) => setPubDateEnd(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Grupo 3: Valor Estimado */}
                        <div className="bg-surface dark:bg-surface-dark p-3 rounded-lg border border-line dark:border-line-dark shadow-sm">
                            <label className="block text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark mb-2 flex items-center gap-1">
                                <DollarSign className="w-3 h-3 xl:w-3.5 xl:h-3.5" /> Valor (R$)
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    placeholder="Min"
                                    value={minValue}
                                    onChange={(e) => setMinValue(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                                <span className="text-ink-placeholder dark:text-ink-placeholder-dark text-[10px] xl:text-xs flex-shrink-0">até</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    value={maxValue}
                                    onChange={(e) => setMaxValue(e.target.value)}
                                    className="w-full text-[10px] xl:text-xs text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong rounded px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Botão de Limpar Filtros Avançados */}
                        <div className="col-span-1 lg:col-span-2 xl:col-span-3 flex justify-end">
                            <button
                                onClick={clearAdvancedFilters}
                                disabled={!activeAdvancedFilters}
                                className="text-[10px] xl:text-xs text-red-600 dark:text-red-300 hover:text-red-800 flex items-center gap-1 disabled:opacity-50 transition-opacity"
                            >
                                <FilterX className="w-3 h-3" /> Limpar filtros avançados
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Tabela / Painéis especiais */}
        {viewMode === 'CONSULTS' && (
          <div className="p-4 xl:p-6">
            <ExternalTenderList
              externalSearchTerm={searchTerm}
              externalSetSearchTerm={setSearchTerm}
              externalStateFilter={stateFilter}
              externalSetStateFilter={setStateFilter}
              externalClearFilters={clearFilters}
              onAddToWallet={handleAddExternalTender}
            />
          </div>
        )}

        {viewMode === 'CONCORRENTE' && (
          <div className="p-4 xl:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-1 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold">Subconsultas</h4>
                  <button
                    onClick={() => { setNewConcorrenteName(''); setConcorrenteQueries([]); }}
                    className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark"
                    title="Limpar">
                    Limpar
                  </button>
                </div>
                <div className="mb-3 flex gap-2">
                  <input value={newConcorrenteName} onChange={(e) => setNewConcorrenteName(e.target.value)} placeholder="Nome da subconsulta" className="flex-1 text-sm border rounded px-2 py-1" />
                  <button
                    onClick={() => {
                      if (newConcorrenteName.trim()) {
                        setConcorrenteQueries(prev => [...prev, newConcorrenteName.trim()]);
                        setNewConcorrenteName('');
                      }
                    }}
                    className="bg-fuchsia-600 text-white px-3 rounded text-sm">
                    Adicionar
                  </button>
                </div>
                <div className="space-y-2">
                  {concorrenteQueries.length === 0 ? (
                    <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Nenhuma subconsulta criada.</div>
                  ) : concorrenteQueries.map((q, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2">
                      <button onClick={() => setSelectedConcorrenteIndex(idx)} className="text-sm text-left truncate w-full text-ink-text dark:text-ink-text-dark hover:text-ink-title dark:text-ink-title-dark">
                        {q}
                      </button>
                      <button onClick={() => setConcorrenteQueries(prev => prev.filter((_, i) => i !== idx))} className="text-xs text-red-600 dark:text-red-300">Remover</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-semibold mb-2">Detalhes / Itens da Consulta</h4>
                {typeof selectedConcorrenteIndex === 'number' && concorrenteQueries[selectedConcorrenteIndex] ? (
                  <div>
                    <p className="text-sm font-medium">{concorrenteQueries[selectedConcorrenteIndex]}</p>
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-2">Aqui você pode adicionar widgets, filtros salvos ou outros itens relacionados à subconsulta selecionada.</p>
                  </div>
                ) : (
                  <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Selecione uma subconsulta à esquerda ou crie uma nova.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modo CARDS — pixel-perfect com classes .lcard do handoff */}
        {viewMode !== 'CONSULTS' && viewMode !== 'CONCORRENTE' && renderMode === 'cards' && (
          <div className="p-4 xl:p-6">
            {currentTenders.length === 0 ? (
              <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
                Nenhuma licitação encontrada com os filtros atuais.
              </div>
            ) : (
              <div className="lic-cards">
                {currentTenders.map(tender => {
                  const daysLeft = daysUntilCalendarDate(tender.deadline);
                  const tone = daysLeft == null ? 'ok' : daysLeft < 0 ? 'past' : daysLeft <= 3 ? 'crit' : daysLeft <= 10 ? 'warn' : daysLeft <= 20 ? 'info' : 'ok';
                  const prazoColorVar = tone === 'crit' ? 'var(--color-danger)' : tone === 'warn' ? 'var(--color-warning)' : tone === 'info' ? 'var(--color-info)' : 'var(--color-ink-title)';
                  const checklistCount = ((tender as any).isRegistered ? 1 : 0) + ((tender as any).areDocsReady ? 1 : 0);
                  const editalNum = (tender.title || '').split(' - ')[0].trim().split(' — ')[0].trim() || `#${tender.id}`;
                  const prioridade = tender.priority === 'ULTRA' ? { cls: 'crit', label: 'Ultra' }
                                   : tender.priority === 'VERY_HIGH' ? { cls: 'warn', label: 'Altíssima' }
                                   : tender.priority === 'PRIORITY' ? { cls: 'info', label: 'Prioridade' }
                                   : null;
                  return (
                    <div key={tender.id} className="lcard" onClick={() => setDetailTender(tender)}>
                      <div className={`rail ${tone}`} />
                      <div className="lcard-head">
                        <span className="num">{editalNum}</span>
                        <span className="uf-tag" style={{ marginLeft: 6 }}>{tender.state || 'N/A'}</span>
                        <div className="right">
                          <button
                            type="button"
                            className="lic-star-btn"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Favoritar"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <h4>{tender.title}</h4>
                      <div className="orgao">
                        <Building2 style={{ width: 11, height: 11, flexShrink: 0, opacity: .7 }} />
                        <span className="txt">{tender.organ}</span>
                      </div>
                      <div className="lcard-meta">
                        <div>
                          <div className="k">Prazo</div>
                          <div className="v" style={{ color: prazoColorVar, fontWeight: 600 }}>
                            {daysLeft == null ? '—' : daysLeft < 0 ? 'Expirado' : `${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`}
                            {tender.deadline && ` · ${formatCalendarDate(tender.deadline)}`}
                          </div>
                        </div>
                        <div>
                          <div className="k">Valor estimado</div>
                          <div className="v">
                            {tender.value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(tender.value as unknown as number) : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="k">Modalidade</div>
                          <div className="v" style={{ fontSize: 12 }}>{tender.portal || '—'}</div>
                        </div>
                        <div>
                          <div className="k">Checklist</div>
                          <div className="v" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                            <span className="tabular">{checklistCount}/2</span>
                            <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--color-surface-alt)' }}>
                              <div style={{ width: `${checklistCount * 50}%`, height: '100%', background: 'var(--color-brand-500)', borderRadius: 3, transition: 'width .3s' }} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="lcard-foot">
                        <div className="pills">
                          {viewMode === 'PIPELINE' || viewMode === 'ONGOING'
                            ? <PrioritySelect priority={tender.priority} onChange={(priority) => handlePriorityChange(tender.id, priority)} />
                            : prioridade && <span className={`lic-pill ${prioridade.cls}`}>{prioridade.label}</span>}
                          <span className="lic-pill">{tender.status}</span>
                        </div>
                        <ArrowRight style={{ width: 14, height: 14, color: 'var(--color-ink-subtitle)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Modo KANBAN — pixel-perfect com .kanban-col / .kcard */}
        {viewMode !== 'CONSULTS' && viewMode !== 'CONCORRENTE' && renderMode === 'kanban' && (() => {
          const COLUMNS: { key: string; label: string; statuses: TenderStatus[]; dotColor: string }[] = [
            { key: 'analise',     label: 'Em análise',        statuses: [TenderStatus.ANALYSIS, TenderStatus.OPEN], dotColor: 'var(--color-info)' },
            { key: 'proposta',    label: 'Proposta enviada',  statuses: [TenderStatus.PARTICIPATE],                 dotColor: 'var(--color-brand-500)' },
            { key: 'aguardando',  label: 'Aguardando sessão', statuses: [TenderStatus.HIGH_PRIORITY],               dotColor: 'var(--color-warning)' },
            { key: 'disputa',     label: 'Em disputa',        statuses: [TenderStatus.EXTREME_PRIORITY],            dotColor: 'var(--color-danger)' },
            { key: 'homologacao', label: 'Homologação',       statuses: [TenderStatus.WON],                         dotColor: 'var(--color-success)' },
          ];
          const now = Date.now();
          const fmtBRL = (v?: number | null) => {
            if (v == null) return '—';
            if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
            if (v >= 1_000) return `R$ ${Math.round(v / 1_000)}K`;
            return `R$ ${v}`;
          };
          return (
            <div className="overflow-x-auto p-4">
              <div className="lic-kanban">
                {COLUMNS.map(col => {
                  const colTenders = currentTenders.filter(t => col.statuses.includes(t.status as TenderStatus));
                  return (
                    <div key={col.key} className="kanban-col">
                      <div className="kanban-head">
                        <span className="dot" style={{ background: col.dotColor }} />
                        <h4>{col.label}</h4>
                        <span className="count">{colTenders.length}</span>
                      </div>
                      <div className="kanban-body">
                        {colTenders.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '24px 8px', fontSize: 11.5, color: 'var(--color-ink-placeholder)' }}>
                            Nenhuma licitação
                          </div>
                        ) : (
                          colTenders.map(t => {
                            const daysLeft = daysUntilCalendarDate(t.deadline);
                            const tone = daysLeft == null ? 'ok' : daysLeft < 0 ? 'crit' : daysLeft <= 3 ? 'crit' : daysLeft <= 10 ? 'warn' : daysLeft <= 20 ? 'info' : 'ok';
                            const editalNum = (t.title || '').split(' - ')[0].trim().split(' — ')[0].trim() || `#${t.id}`;
                            return (
                              <div
                                key={t.id}
                                className="kcard"
                                onClick={() => setDetailTender(t)}
                              >
                                <div className="kcard-head">
                                  <span className="kcard-num">{editalNum}</span>
                                  <span className="uf-tag" style={{ marginLeft: 'auto', fontSize: 10, height: 18, minWidth: 26 }}>{t.state || 'N/A'}</span>
                                </div>
                                <div className="kcard-title">{t.title}</div>
                                <div className="kcard-orgao">{t.organ || '—'}</div>
                                <div className="kcard-foot">
                                  <div className="left">
                                    <span className={`kcard-prazo ${tone}`}>
                                      {daysLeft == null ? '—' : daysLeft < 0 ? 'expirou' : `${daysLeft}d`}
                                    </span>
                                  </div>
                                  <span className="kcard-val">{fmtBRL(t.value as unknown as number)}</span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Modo LISTA — pixel-perfect com .lic-row do handoff (direção B) */}
        {viewMode !== 'CONSULTS' && viewMode !== 'CONCORRENTE' && renderMode === 'list' && (
          <div className="p-4 xl:p-6">
            {currentTenders.length === 0 ? (
              <div className="py-20 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Search className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  <p>Nenhuma licitação encontrada nesta categoria.</p>
                  <p className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Verifique os filtros ou alterne a aba superior.</p>
                </div>
              </div>
            ) : (
              <div className="lic-list">
                {currentTenders.map(tender => {
                  const daysLeft = daysUntilCalendarDate(tender.deadline);
                  const tone = daysLeft == null ? 'ok'
                    : daysLeft < 0 ? 'past'
                    : daysLeft <= 3 ? 'crit'
                    : daysLeft <= 10 ? 'warn'
                    : daysLeft <= 20 ? 'info'
                    : 'ok';
                  const railCls = tender.priority === 'ULTRA' ? 'critical'
                    : tender.priority === 'VERY_HIGH' ? 'alta'
                    : tender.priority === 'PRIORITY' ? 'media'
                    : 'baixa';
                  const editalNum = (tender.title || '').split(' - ')[0].trim().split(' — ')[0].trim() || `#${tender.id}`;
                  const objeto = (tender.title || '').replace(editalNum, '').replace(/^[\s—\-·]+/, '').trim() || tender.title;
                  const statusPillCls = tender.status === TenderStatus.EXTREME_PRIORITY ? 'crit'
                    : tender.status === TenderStatus.HIGH_PRIORITY ? 'warn'
                    : tender.status === TenderStatus.WON ? 'succ'
                    : tender.status === TenderStatus.ANALYSIS ? 'info'
                    : tender.status === TenderStatus.PARTICIPATE ? 'prim'
                    : '';
                  const checklistCount = ((tender as any).isRegistered ? 5 : 0) + ((tender as any).areDocsReady ? 5 : 0);
                  const prazoStyle: React.CSSProperties = tone === 'crit'
                    ? { color: 'var(--color-danger)' }
                    : tone === 'warn'
                      ? { color: 'var(--color-warning)' }
                      : {};
                  return (
                    <div
                      key={tender.id}
                      className="lic-row"
                      onClick={() => setDetailTender(tender)}
                    >
                      {/* Rail de prioridade */}
                      <span className={`rail ${railCls}`} />

                      {/* Cápsula countdown */}
                      <div className={`countdown ${tone}`}>
                        <div className="n">{daysLeft == null ? '—' : daysLeft < 0 ? '—' : Math.abs(daysLeft)}</div>
                        <div className="u">{daysLeft == null ? '' : daysLeft < 0 ? 'expir.' : daysLeft === 0 ? 'HOJE' : daysLeft === 1 ? 'DIA' : 'DIAS'}</div>
                      </div>

                      {/* Main: título + sub */}
                      <div className="main">
                        <div className="title">
                          <span>{editalNum}{objeto && objeto !== editalNum ? ` — ${objeto}` : ''}</span>
                        </div>
                        <div className="sub">
                          <Building2 style={{ width: 11, height: 11 }} />
                          <span className="orgao">{tender.organ}</span>
                          {tender.portal && (
                            <>
                              <span className="sep">·</span>
                              <span>{tender.portal}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* UF tag */}
                      <span className="uf-tag">{tender.state || 'N/A'}</span>

                      {/* Datas: publicação */}
                      <div className="dates">
                        <span className="label">Publicação</span>
                        <span className="val">{formatCalendarDate(tender.publicationDate)}</span>
                      </div>

                      {/* Datas: prazo final */}
                      <div className="dates">
                        <span className="label">Prazo final</span>
                        <span className="val" style={{ ...prazoStyle, fontWeight: 600 }}>
                          {formatCalendarDate(tender.deadline)}
                        </span>
                      </div>

                      {/* Prioridade editável na carteira; status operacional continua separado. */}
                      <div>
                        {viewMode === 'PIPELINE' || viewMode === 'ONGOING'
                          ? <PrioritySelect priority={tender.priority} onChange={(priority) => handlePriorityChange(tender.id, priority)} />
                          : <span className={`lic-pill ${statusPillCls}`}>{tender.status}</span>}
                      </div>

                      {/* Checklist mini */}
                      <div className="checklist-mini">
                        <div className="label">
                          <span>Checklist</span>
                          <span>{checklistCount}/10</span>
                        </div>
                        <div className="bar">
                          <div className={`fill ${checklistCount === 10 ? 'done' : ''}`} style={{ width: `${checklistCount * 10}%` }} />
                        </div>
                        <div className="items">
                          <span
                            className={`i ${(tender as any).isRegistered ? 'on' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleToggleChecklist(tender.id, 'isRegistered'); }}
                            title="Cadastrado"
                            style={{ cursor: 'pointer' }}
                          >
                            <span className="d" /> Cadastrado
                          </span>
                          <span
                            className={`i ${(tender as any).areDocsReady ? 'on' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleToggleChecklist(tender.id, 'areDocsReady'); }}
                            title="Docs prontos"
                            style={{ cursor: 'pointer' }}
                          >
                            <span className="d" /> Docs
                          </span>
                        </div>
                      </div>

                      {/* Ações: favoritar + more */}
                      <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="lic-star-btn"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Favoritar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="lic-more-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setActionsAnchorRect({ top: r.bottom, left: r.right });
                            setOpenActionsId(openActionsId === tender.id ? null : tender.id);
                          }}
                          aria-label="Mais opções"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                        {openActionsId === tender.id && actionsAnchorRect && createPortal(
                          <div
                            className="w-44 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md shadow-lg z-50 actions-menu"
                            style={{ position: 'fixed', top: actionsAnchorRect.top + 8, left: Math.max(actionsAnchorRect.left - 176, 8) }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleOpenScheduleModal(tender); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center gap-2"><CalendarDays className="w-4 h-4" />{tender.nextActionDate ? 'Editar' : 'Agendar'}</button>
                            <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); setDetailTender(tender); setIsDetailModalOpen(true); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center gap-2"><Eye className="w-4 h-4" />Visualizar</button>
                            <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); openAttachmentsModal(tender); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt flex items-center gap-2"><FileText className="w-4 h-4" />Anexos</button>
                            {tender.fileUrl && (<a href={buildDocumentOpenUrl(tender.fileUrl, tender.id)} target="_blank" rel="noopener noreferrer" onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); }} className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2"><Eye className="w-4 h-4" />{isExternalDocumentUrl(tender.fileUrl) ? 'Abrir edital no portal' : 'Ver PDF'}</a>)}
                            {(() => { const u = localStorage.getItem('user'); const p = u ? JSON.parse(u) : null; const canDelete = p && (p.role === 'ADMIN' || p.role === 'MANAGER'); return canDelete ? (<button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleDeleteTender(tender.id); }} className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"><X className="w-4 h-4" />Excluir</button>) : null; })()}
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Placeholder para fallback da versão antiga (nunca renderizado, mantido vazio) */}
            <div style={{ display: 'none' }}>
              <div className="hidden md:block">
                <div className="min-w-full inline-block align-middle">
                <div className="overflow-hidden">
                  <table className="w-full text-left border-collapse relative table-fixed tender-table">
                    <colgroup>
                      <col className="col-1" />
                      <col className="col-2" />
                      <col className="col-3" />
                      <col className="col-4" />
                      <col className="col-5" />
                      <col className="col-6" />
                      <col className="col-7" />
                      <col className="col-8" />
                    </colgroup>
                    <thead className="sticky top-0 z-10 bg-surface-alt dark:bg-surface-dark-alt shadow-sm">
                      <tr>
                        <th className="py-2 xl:py-3 px-3 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark">Objeto / Órgão</th>
                        <th className="py-2 xl:py-3 px-2 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark text-center">UF</th>
                        <th className="py-2 xl:py-3 px-2 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark text-center hidden xl:table-cell">Publicação {sortBy === 'PUBLICATION_DATE' && <ArrowUpDown className="w-3 h-3 text-blue-500 dark:text-blue-400 inline ml-1" />}</th>
                        <th className="py-2 xl:py-3 px-3 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark">Prazo {sortBy === 'DEADLINE' && <ArrowUpDown className="w-3 h-3 text-blue-500 dark:text-blue-400 inline ml-1" />}</th>
                        <th className="py-2 xl:py-3 px-2 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark text-center">Portal</th>
                        <th className="py-2 xl:py-3 px-3 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark">{(isOngoingView || viewMode === 'ONGOING' || viewMode === 'PIPELINE') ? 'Prioridade' : 'Status'}</th>
                        <th className="py-2 xl:py-3 px-2 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark text-center hidden lg:table-cell">Checklist</th>
                        <th className="py-2 xl:py-3 px-2 xl:px-6 text-[10px] xl:text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider border-b border-line dark:border-line-dark text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-surface dark:bg-surface-dark">
                      {currentTenders.length > 0 ? (
                        currentTenders.map((tender) => {
                          // Lógica de destaque visual
                          const isExtreme = tender.status === TenderStatus.EXTREME_PRIORITY;
                          const isHigh = tender.status === TenderStatus.HIGH_PRIORITY;
                          const isWon = tender.status === TenderStatus.WON;
                          const isNoGo = tender.status === TenderStatus.NO_GO;
                          const isLost = tender.status === TenderStatus.LOST;
                          const isQuote = tender.status === TenderStatus.ELECTRONIC_QUOTE;
                          const isWaiver = tender.status === TenderStatus.DIRECT_WAIVER;
                          const isRandom = tender.status === TenderStatus.RANDOM;

                          let rowClass = "hover:bg-surface-alt dark:bg-surface-dark-alt/50 transition-colors group border-l-4 border-l-transparent";

                          if (isExtreme) rowClass = "bg-red-50/60 dark:bg-red-900/15 hover:bg-red-100/60 dark:hover:bg-red-900/25 border-l-4 border-l-red-500 dark:border-l-red-400 transition-colors group";
                          else if (isHigh) rowClass = "bg-orange-50/60 dark:bg-orange-900/15 hover:bg-orange-100/60 dark:hover:bg-orange-900/25 border-l-4 border-l-orange-500 dark:border-l-orange-400 transition-colors group";
                          else if (isWon) rowClass = "bg-emerald-50/40 dark:bg-emerald-900/15 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/25 border-l-4 border-l-emerald-500 dark:border-l-emerald-400 transition-colors group";
                          else if (isLost) rowClass = "bg-surface-alt dark:bg-surface-dark-alt/60 opacity-60 hover:opacity-100 transition-colors group";
                          else if (isNoGo) rowClass = "bg-slate-50 dark:bg-slate-900/20 opacity-60 hover:opacity-100 transition-colors group";
                          else if (isQuote) rowClass = "bg-indigo-50/40 dark:bg-indigo-900/15 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/25 border-l-4 border-l-indigo-500 dark:border-l-indigo-400 transition-colors group";
                          else if (isWaiver) rowClass = "bg-teal-50 dark:bg-teal-900/15 hover:bg-teal-100 dark:hover:bg-teal-900/25 border-l-4 border-l-teal-500 dark:border-l-teal-400 transition-colors group";
                          else if (isRandom) rowClass = "bg-fuchsia-50 dark:bg-fuchsia-900/15 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-900/25 border-l-4 border-l-fuchsia-500 dark:border-l-fuchsia-400 transition-colors group";

                          return (
                            <tr key={tender.id} className={rowClass}>
                              <td className="py-2 xl:py-3 px-3 xl:px-6 align-middle">
                                <div className="flex flex-col">
                                  <TruncatedCell lines={2} text={tender.title} className="font-medium text-[11px] xl:text-sm text-ink-title dark:text-ink-title-dark" />
                                  <div className="text-[10px] xl:text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-1 mt-0.5">
                                    <Building2 className="w-2.5 h-2.5 xl:w-3 xl:h-3 flex-shrink-0" />
                                    <TruncatedCell text={tender.organ} className="block max-w-[240px]" />
                                  </div>
                                  <span className="text-[10px] xl:text-[11px] text-ink-placeholder dark:text-ink-placeholder-dark font-medium mt-0.5">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(tender.value)}</span>
                                </div>
                              </td>
                              <td className="py-2 xl:py-3 px-2 xl:px-6 align-middle text-center"><span className="inline-flex items-center px-1.5 xl:px-2 py-0.5 xl:py-1 rounded text-[10px] xl:text-xs font-bold bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark border border-line dark:border-line-dark">{tender.state || 'N/A'}</span></td>
                              <td className="py-2 xl:py-3 px-2 xl:px-6 align-middle text-center hidden xl:table-cell"><div className="flex items-center justify-center gap-1 text-[11px] xl:text-sm text-ink-subtitle dark:text-ink-subtitle-dark"><FileText className="w-3 h-3 xl:w-3.5 xl:h-3.5 opacity-50" />{formatCalendarDate(tender.publicationDate, '-')}</div></td>
                              <td className="py-2 xl:py-3 px-3 xl:px-6 align-middle"><div className={`flex items-center gap-1 text-[11px] xl:text-sm ${isExtreme ? 'text-red-600 dark:text-red-400 font-bold' : isHigh ? 'text-orange-600 dark:text-orange-400 font-semibold' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}><Calendar className="w-3 h-3 xl:w-4 xl:h-4 opacity-70 flex-shrink-0" /><span className="whitespace-nowrap">{formatCalendarDate(tender.deadline)}</span></div>{tender.nextActionDate && (<div className="text-[9px] xl:text-[10px] text-blue-600 dark:text-blue-300 font-medium flex items-center gap-1 mt-0.5" title={`Próxima Ação: ${tender.nextAction}`}><Clock className="w-2.5 h-2.5 xl:w-3 xl:h-3 flex-shrink-0" /><span className="whitespace-nowrap">{formatCalendarDate(tender.nextActionDate)}</span></div>)}</td>
                              <td className="py-2 xl:py-3 px-2 xl:px-6 align-middle text-center"><PortalCell url={undefined} label={tender.portal} /></td>
                              <td className="py-2 xl:py-3 px-3 xl:px-6 align-middle">
                                {viewMode === 'PIPELINE' || viewMode === 'ONGOING'
                                  ? <PrioritySelect priority={tender.priority} onChange={(priority) => handlePriorityChange(tender.id, priority)} />
                                  : <StatusSelect status={tender.status} onChange={(newStatus) => handleStatusChange(tender.id, newStatus)} />}
                              </td>
                              <td className="py-2 xl:py-3 px-2 xl:px-6 align-middle hidden lg:table-cell"><div className="flex flex-row lg:flex-col gap-2 xl:gap-2 items-start">{/* checkboxes preserved */}
                                {/* Checkbox 1: Cadastrado */}
                                  <label className="flex items-center gap-1.5 xl:gap-2 cursor-pointer group/chk select-none" title="Marcar se a licitação já foi cadastrada no portal">
                                  <div className="relative flex items-center">
                                    <input type="checkbox" checked={tender.isRegistered || false} onChange={() => handleToggleChecklist(tender.id, 'isRegistered')} className="peer h-3.5 w-3.5 xl:h-4 xl:w-4 cursor-pointer appearance-none rounded border border-line dark:border-line-dark shadow-sm checked:border-brand-500 checked:bg-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all" />
                                    <Check className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white w-2.5 h-2.5 xl:w-3 xl:h-3 opacity-0 peer-checked:opacity-100 transition-opacity" />
                                  </div>
                                  <span title={tender.isRegistered ? 'Cadastrado' : 'Não cadastrado'} className={`text-[10px] xl:text-[11px] font-medium transition-colors whitespace-nowrap checklist-label ${tender.isRegistered ? 'text-blue-700 dark:text-blue-300' : 'text-ink-subtitle dark:text-ink-subtitle-dark group-hover/chk:text-ink-text dark:text-ink-text-dark'}`}>Cadastrado</span>
                                </label>
                                {/* Checkbox 2: Docs */}
                                <label className="flex items-center gap-1.5 xl:gap-2 cursor-pointer group/chk select-none" title="Indicar se os documentos relevantes estão prontos">
                                  <div className="relative flex items-center">
                                    <input type="checkbox" checked={tender.areDocsReady || false} onChange={() => handleToggleChecklist(tender.id, 'areDocsReady')} className="peer h-3.5 w-3.5 xl:h-4 xl:w-4 cursor-pointer appearance-none rounded border border-line-strong dark:border-line-dark-strong shadow-sm checked:border-green-600 checked:bg-green-600 focus:ring-2 focus:ring-green-500/20 transition-all" />
                                    <Check className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white w-2.5 h-2.5 xl:w-3 xl:h-3 opacity-0 peer-checked:opacity-100 transition-opacity" />
                                  </div>
                                  <span title={tender.areDocsReady ? 'Documentos prontos' : 'Documentos não prontos'} className={`text-[10px] xl:text-[11px] font-medium transition-colors whitespace-nowrap checklist-label ${tender.areDocsReady ? 'text-green-700 dark:text-green-300' : 'text-ink-subtitle dark:text-ink-subtitle-dark group-hover/chk:text-ink-text dark:text-ink-text-dark'}`}>Docs Prontos</span>
                                </label>
                              </div></td>
                              <td className="py-2 xl:py-3 px-2 xl:px-6 text-right align-middle"><div className="flex justify-end gap-1 xl:gap-2">
                                {/* botão de agendamento movido para o menu de ações para economizar espaço */}
                                <div className="relative">
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    const btn = e.currentTarget as HTMLElement;
                                    const r = btn.getBoundingClientRect();
                                    setActionsAnchorRect({ top: r.bottom, left: r.right });
                                    setOpenActionsId(openActionsId === tender.id ? null : tender.id);
                                  }} className="p-1 xl:p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded-lg transition-colors actions-button" title="Mais Opções"><MoreHorizontal className="w-3.5 h-3.5 xl:w-4 xl:h-4" /></button>
                                  {openActionsId === tender.id && actionsAnchorRect && createPortal(
                                    <div
                                      className="w-44 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md shadow-lg z-50 actions-menu"
                                      style={{ position: 'fixed', top: actionsAnchorRect.top + 8, left: Math.max(actionsAnchorRect.left - 176, 8) }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleOpenScheduleModal(tender); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><CalendarDays className="w-4 h-4" />{tender.nextActionDate ? 'Editar' : 'Agendar'}</button>
                                      <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); setDetailTender(tender); setIsDetailModalOpen(true); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><Eye className="w-4 h-4" />Visualizar</button>
                                      <button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); openAttachmentsModal(tender); }} className="w-full text-left px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt flex items-center gap-2"><FileText className="w-4 h-4" />Anexos Complementares</button>
                                      {tender.fileUrl && (<a href={buildDocumentOpenUrl(tender.fileUrl, tender.id)} target="_blank" rel="noopener noreferrer" onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); }} className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-300 hover:bg-blue-50 flex items-center gap-2"><Eye className="w-4 h-4" />{isExternalDocumentUrl(tender.fileUrl) ? 'Abrir edital no portal' : 'Ver PDF'}</a>)}
                                      {(() => { const user = localStorage.getItem('user'); const parsed = user ? JSON.parse(user) : null; const canDelete = parsed && (parsed.role === 'ADMIN' || parsed.role === 'MANAGER'); return canDelete ? (<button onClick={() => { setOpenActionsId(null); setActionsAnchorRect(null); handleDeleteTender(tender.id); }} className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 flex items-center gap-2"><X className="w-4 h-4" />Excluir</button>) : null; })()}
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              </div></td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="py-12 text-center text-ink-subtitle dark:text-ink-subtitle-dark text-sm">
                            <div className="flex flex-col items-center justify-center gap-2">
                              <Search className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark" />
                              <p>Nenhuma licitação encontrada nesta categoria.</p>
                              <p className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Verifique os filtros ou alterne a aba superior.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden p-4 space-y-3">
              {currentTenders.length > 0 ? (
                currentTenders.map((t) => (
                  <article key={t.id} className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <TruncatedCell text={t.title} className="font-medium text-sm" />
                        <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1 flex items-center gap-2"><Building2 className="w-3 h-3" /> <TruncatedCell text={t.organ} className="max-w-[160px]" /></div>
                        <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(t.value)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs font-semibold">{t.state || 'N/A'}</span>
                        <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{formatCalendarDate(t.deadline)}</span>
                        <div className="flex items-center gap-1"><PortalCell url={undefined} label={t.portal} /></div>
                      </div>
                    </div>
                    <details className="mt-2">
                      <summary className="text-xs text-blue-600 dark:text-blue-300 cursor-pointer">Mais detalhes</summary>
                      <div className="mt-2 text-sm text-ink-text dark:text-ink-text-dark">
                        <div><strong>Status:</strong> {t.status}</div>
                        <div className="mt-1"><strong>Publicação:</strong> {formatCalendarDate(t.publicationDate, '-')}</div>
                      </div>
                    </details>
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => openAttachmentsModal(t)} className="text-xs text-blue-600 dark:text-blue-300 hover:underline">Anexos Complementares</button>
                    </div>
                  </article>
                ))
              ) : (
                <div className="text-center text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma licitação encontrada.</div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* Footer / Paginação - RESPONSIVO OTIMIZADO */}
        <div className="p-3 xl:p-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt flex flex-col sm:flex-row justify-between items-center gap-3 xl:gap-4 text-xs xl:text-sm flex-shrink-0">
          <div className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark">
            <span className="text-[10px] xl:text-sm">Itens/página:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong text-ink-text dark:text-ink-text-dark text-[10px] xl:text-sm rounded-md focus:ring-brand-500 focus:border-brand-500 block p-1 outline-none"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="hidden lg:inline text-ink-placeholder dark:text-ink-placeholder-dark">|</span>
            <span className="hidden lg:inline text-[10px] xl:text-sm">
              Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredTenders.length)} - {Math.min(currentPage * itemsPerPage, filteredTenders.length)} de {filteredTenders.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 xl:p-1.5 border border-line-strong dark:border-line-dark-strong rounded-md hover:bg-surface dark:bg-surface-dark disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
            </button>
            <span className="text-ink-subtitle dark:text-ink-subtitle-dark font-medium text-[10px] xl:text-sm whitespace-nowrap">
              Pág {currentPage} de {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 xl:p-1.5 border border-line-strong dark:border-line-dark-strong rounded-md hover:bg-surface dark:bg-surface-dark disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Visualização de Licitação (detalhes) */}
      {isDetailModalOpen && detailTender && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600 dark:text-blue-300" /> Detalhes da Licitação
              </h3>
              <button onClick={() => setIsDetailModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <h4 className="font-medium text-lg">{detailTender.title}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm text-ink-text dark:text-ink-text-dark">
                <div><strong>Órgão:</strong> {detailTender.organ}</div>
                <div><strong>UF:</strong> {detailTender.state || 'N/A'}</div>
                <div><strong>Portal:</strong> {detailTender.portal || '-'}</div>
                <div><strong>Valor Estimado:</strong> {formatBRLCurrency(detailTender.value)}</div>
                <div><strong>Prazo:</strong> {formatCalendarDate(detailTender.deadline)}</div>
                <div><strong>Publicação:</strong> {formatCalendarDate(detailTender.publicationDate, '-')}</div>
              </div>
              {detailTender.fileUrl && (
                <div className="pt-2 border-t border-line dark:border-line-dark">
                  <a
                    href={buildDocumentOpenUrl(detailTender.fileUrl, detailTender.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    {isExternalDocumentUrl(detailTender.fileUrl) ? 'Abrir edital no portal' : 'Ver PDF do Edital'}
                  </a>
                </div>
              )}
              <div className="pt-2 border-t border-line dark:border-line-dark text-sm text-ink-text dark:text-ink-text-dark">
                <strong>Descrição:</strong>
                <p className="mt-2 whitespace-pre-wrap">{detailTender.description || '-'}</p>
              </div>
            </div>
            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
              <button onClick={() => setIsDetailModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Anexos Complementares */}
      {isAttachmentsModalOpen && attachmentsTender && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-surface dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-scale-in">
            <div className="flex items-center gap-4 p-5 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-md p-2">
                  <FileText className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark">Anexos Complementares</h3>
                  <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{attachmentsTender.organ} • {attachmentsTender.state || 'N/A'}</div>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <label className={`flex items-center gap-2 px-3 py-2 border border-line dark:border-line-dark rounded-md bg-surface dark:bg-surface-dark cursor-pointer text-sm shadow-sm hover:bg-surface-alt dark:bg-surface-dark-alt ${isUploadingInAttachmentsModal ? 'opacity-60 cursor-wait' : ''}`}>
                  <input type="file" className="hidden" accept=".pdf" multiple onChange={handleAttachmentsModalUpload} disabled={isUploadingInAttachmentsModal} />
                  <Upload className="w-4 h-4 text-ink-text dark:text-ink-text-dark" />
                  <span className="text-sm text-ink-text dark:text-ink-text-dark">Adicionar PDF</span>
                </label>
                <button onClick={() => { setIsAttachmentsModalOpen(false); setAttachmentsTender(null); setTenderAttachments([]); }} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark p-2 rounded-md"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-6">
              {isLoadingAttachments ? (
                <div className="flex items-center justify-center py-12 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Carregando anexos...</div>
              ) : tenderAttachments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-16 h-16 rounded-lg bg-surface-alt dark:bg-surface-dark-alt flex items-center justify-center border border-dashed border-line dark:border-line-dark">
                    <Paperclip className="w-6 h-6 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  </div>
                  <div className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark font-medium">Nenhum anexo encontrado.</div>
                  <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Você pode adicionar arquivos PDF para vincular a esta licitação.</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {tenderAttachments.map(att => (
                    <div key={att.id} className="flex items-center justify-between gap-4 p-3 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg shadow-sm">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="bg-surface-alt dark:bg-surface-dark-alt p-2 rounded-md flex-shrink-0">
                          <Paperclip className="w-5 h-5 text-ink-subtitle dark:text-ink-subtitle-dark" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-ink-title dark:text-ink-title-dark truncate">{att.name}</div>
                          <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark truncate">{att.size ? `${Math.round((att.size/1024))} KB` : ''} {att.uploadedAt ? `• ${new Date(att.uploadedAt).toLocaleString()}` : ''}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(att as any).sourceExternalId && <span className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">Externo</span>}
                        <a href={buildDocumentOpenUrl(att.url, (att as any).sourceExternalId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-sm text-blue-600 dark:text-blue-300 rounded hover:bg-blue-50">
                          <Eye className="w-4 h-4" />
                          Abrir
                        </a>
                        <button onClick={() => handleDeleteAttachment(att.id)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-sm text-rose-600 rounded hover:bg-rose-50">
                          <X className="w-4 h-4" />
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
              <button onClick={() => { setIsAttachmentsModalOpen(false); setAttachmentsTender(null); setTenderAttachments([]); }} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Nova Licitação */}
      {isNewTenderModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                  <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                      <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                          <Plus className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          Nova Licitação
                      </h3>
                      <button onClick={() => setIsNewTenderModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                          <X className="w-5 h-5" />
                      </button>
                  </div>

                  <div className="p-6 overflow-y-auto space-y-4">
                      {/* Área de Upload Inteligente */}
                      <div className="mb-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 rounded-xl p-4">
                          <div className="flex items-start gap-3">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg text-blue-600 dark:text-blue-300">
                                  <Sparkles className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                  <h4 className="text-sm font-bold text-blue-900 mb-1">Preenchimento Automático com IA</h4>
                                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">Faça upload do PDF do edital e a IA preencherá os campos automaticamente para você.</p>

                                  <label className={`flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer transition-colors ${isExtracting ? 'bg-blue-100 dark:bg-blue-900/40 opacity-70 cursor-wait' : 'bg-surface dark:bg-surface-dark hover:bg-blue-50 hover:border-blue-400'}`}>
                                      <input type="file" className="hidden" accept=".pdf" onChange={handleTenderFileUpload} disabled={isExtracting} />
                                      {isExtracting ? (
                                          <>
                                              <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-300" />
                                              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Analisando documento...</span>
                                          </>
                                      ) : (
                                          <>
                                              <Upload className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                                              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Clique para carregar PDF da Licitação</span>
                                          </>
                                      )}
                                  </label>
                              </div>
                          </div>
                      </div>

            {/* Anexos Adicionais (Contratos, Empenhos, etc) */}
            <div className="mb-4 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-xl p-3">
              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-2">Anexos Adicionais (Contratos / Empenhos)</label>
              <div className="flex items-center gap-3">
                <label className={`flex items-center gap-2 px-3 py-2 border rounded bg-surface dark:bg-surface-dark cursor-pointer text-sm ${isUploadingAttachments ? 'opacity-60 cursor-wait' : 'hover:bg-surface-alt dark:bg-surface-dark-alt'}`}>
                  <input type="file" accept=".pdf" multiple className="hidden" onChange={handleAdditionalFilesUpload} disabled={isUploadingAttachments} />
                  <Upload className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                  <span className="text-xs text-ink-text dark:text-ink-text-dark">Adicionar arquivos PDF</span>
                </label>
                {isUploadingAttachments && <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Enviando...</span>}
              </div>

              {newTenderAttachments.length > 0 && (
                <ul className="mt-3 space-y-2 text-sm">
                  {newTenderAttachments.map((att, idx) => (
                    <li key={`${att.url}-${idx}`} className="flex items-center justify-between gap-2 bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark rounded px-3 py-2">
                      <div className="truncate">{att.name}</div>
                      <div className="flex items-center gap-2">
                          <a href={buildDocumentOpenUrl(att.url, att.sourceExternalId)} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-300 hover:underline text-xs">Abrir</a>
                        <button type="button" onClick={() => handleRemoveNewAttachment(idx)} className="text-rose-500 text-xs">Remover</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

                      {/* Título */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Objeto / Título *</label>
                          <input
                              type="text"
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                              placeholder="Ex: Aquisição de Computadores - Pregão 01/2023"
                              value={newTenderForm.title}
                              onChange={e => setNewTenderForm({...newTenderForm, title: e.target.value})}
                          />
                      </div>

                      {/* Órgão e Valor */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Órgão Licitante *</label>
                              <input
                                  type="text"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  placeholder="Ex: Prefeitura de São Paulo"
                                  value={newTenderForm.organ}
                                  onChange={e => setNewTenderForm({...newTenderForm, organ: e.target.value})}
                              />
                          </div>
              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Valor Estimado (R$)</label>
                <MoneyInput
                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                  placeholder="0,00"
                  value={typeof newTenderForm.value === 'number' ? newTenderForm.value : null}
                  onValueChange={(v) => setNewTenderForm({...newTenderForm, value: v ?? 0})}
                />
              </div>
                      </div>

                      {/* Portal e UF */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Portal</label>
                              <select
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                  value={newTenderForm.portal}
                                  onChange={e => setNewTenderForm({...newTenderForm, portal: e.target.value})}
                              >
                                  <option value="">Selecione...</option>
                                  {PORTALS.map(p => <option key={p} value={p}>{p}</option>)}
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Estado (UF)</label>
                              <select
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                  value={newTenderForm.state}
                                  onChange={e => setNewTenderForm({...newTenderForm, state: e.target.value})}
                              >
                                  <option value="">Selecione...</option>
                                  {BRAZIL_STATES.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                              </select>
                          </div>
                      </div>

                      {/* Datas */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data de Publicação</label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={newTenderForm.publicationDate}
                                  onChange={e => setNewTenderForm({...newTenderForm, publicationDate: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Prazo / Abertura *</label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/30"
                                  value={newTenderForm.deadline}
                                  onChange={e => setNewTenderForm({...newTenderForm, deadline: e.target.value})}
                              />
                          </div>
                      </div>

                      {/* Status */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Status Inicial</label>
                          <select
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                              value={newTenderForm.status}
                              onChange={e => setNewTenderForm({...newTenderForm, status: e.target.value as TenderStatus})}
                          >
                              {Object.values(TenderStatus).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                      </div>

                      {/* Descrição */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Descrição / Detalhes</label>
                          <textarea
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none h-20"
                              placeholder="Informações adicionais..."
                              value={newTenderForm.description}
                              onChange={e => setNewTenderForm({...newTenderForm, description: e.target.value})}
                          />
                      </div>
                  </div>

                  <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                      <button
                          onClick={() => setIsNewTenderModalOpen(false)}
                          className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
                      >
                          Cancelar
                      </button>
                      <button
                          onClick={handleSaveNewTender}
                          className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
                      >
                          <Save className="w-4 h-4" />
                          Salvar Licitação
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Modal de Configuração de E-mail */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                    Configurar Alertas
                </h3>
                <button onClick={() => setIsEmailModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="p-6">
                <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mb-4">
                  Defina os endereços de e-mail que receberão notificações quando uma licitação for marcada como <strong>Alta Prioridade</strong>, <strong>Altíssima Prioridade</strong> ou <strong>Participar</strong>.
                </p>

                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Destinatários (separe por vírgula)</label>
                <input
                    type="text"
                    className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                    placeholder="ex: equipe@empresa.com, diretor@empresa.com"
                    value={tempEmailInput}
                    onChange={(e) => setTempEmailInput(e.target.value)}
                />

                {/* NEW SECTION: Team List Simulation */}
                <div className="mt-3">
                    <label className="block text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark mb-1.5">Adicionar Equipe (Simulação):</label>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { label: 'Diretoria', email: 'diretoria@empresa.com' },
                            { label: 'Jurídico', email: 'juridico@empresa.com' },
                            { label: 'Financeiro', email: 'financeiro@empresa.com' },
                            { label: 'Engenharia', email: 'engenharia@empresa.com' }
                        ].map((item) => (
                            <button
                                key={item.email}
                                onClick={() => {
                                    if (!tempEmailInput.includes(item.email)) {
                                        setTempEmailInput(prev => prev ? `${prev}, ${item.email}` : item.email);
                                    }
                                }}
                                className="text-xs bg-surface-alt dark:bg-surface-dark-alt hover:bg-blue-50 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-blue-600 px-2.5 py-1.5 rounded-full border border-line dark:border-line-dark transition-colors flex items-center gap-1"
                                type="button"
                            >
                                <Plus className="w-3 h-3" />
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-4 border-t border-line dark:border-line-dark pt-2">
                  * O sistema enviará um alerta real (se configurado) ou simulado no log de auditoria.
                </p>
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                <button
                    onClick={() => setIsEmailModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleSaveEmails}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
                >
                    <Save className="w-4 h-4" />
                    Salvar Configuração
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Agendamento */}
      {isScheduleModalOpen && schedulingTender && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                    Agendar Ação
                </h3>
                <button onClick={() => setIsScheduleModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="p-6 space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg text-xs text-blue-800 dark:text-blue-200 border border-blue-100 mb-4">
                    Agendando para: <strong>{schedulingTender.title}</strong>
                </div>

                {/* Seção de Checklist */}
                <div className="mb-4 bg-surface-alt dark:bg-surface-dark-alt p-3 rounded-lg border border-line dark:border-line-dark">
                    <span className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider mb-2 flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" />
                        Checklist de Preparação
                    </span>
                    <div className="flex flex-col gap-2">
                        {(() => {
                            // Encontrar o estado atual real da licitação na lista
                            const liveTender = tenders.find(t => t.id === schedulingTender.id) || schedulingTender;

                            return (
                                <>
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={liveTender.isRegistered || false}
                                            onChange={() => handleToggleChecklist(liveTender.id, 'isRegistered')}
                                            className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded border-line-strong dark:border-line-dark-strong focus:ring-brand-500 cursor-pointer"
                                        />
                                        <span className={`text-sm ${liveTender.isRegistered ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}>
                                            Cadastro no Portal Realizado
                                        </span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={liveTender.areDocsReady || false}
                                            onChange={() => handleToggleChecklist(liveTender.id, 'areDocsReady')}
                                            className="w-4 h-4 text-green-600 dark:text-green-300 rounded border-line-strong dark:border-line-dark-strong focus:ring-green-500 cursor-pointer"
                                        />
                                        <span className={`text-sm ${liveTender.areDocsReady ? 'text-green-700 dark:text-green-300 font-medium' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}>
                                            Documentos Separados / Prontos
                                        </span>
                                    </label>
                                </>
                            );
                        })()}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Próxima Ação (Descrição)</label>
                    <input
                        type="text"
                        className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                        placeholder="Ex: Enviar Documentação, Disputa de Lances..."
                        value={scheduleForm.action}
                        onChange={(e) => setScheduleForm({...scheduleForm, action: e.target.value})}
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data</label>
                        <input
                            type="date"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            value={scheduleForm.date}
                            onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Horário</label>
                        <input
                            type="time"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            value={scheduleForm.time}
                            onChange={(e) => setScheduleForm({...scheduleForm, time: e.target.value})}
                        />
                    </div>
                </div>
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                <button
                    onClick={() => setIsScheduleModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleSaveSchedule}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
                >
                    <Save className="w-4 h-4" />
                    Salvar na Agenda
                </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TenderList;
